import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateInput from "@/components/DateInput";
import { useLocation, useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtDate, fmtNum, todayISO, calcVencimiento, snapIvaAfip } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getEffectiveListaId } from "@/lib/effectiveListaPrecio";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Trash, UploadSimple, PencilSimple, CaretDown, CaretRight,
  FunnelSimple, X, FileXls, Eye, ShieldCheck, Certificate, FilePdf,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import BatchDetailDialog from "@/components/BatchDetailDialog";
import EmitirRECDialog from "@/components/rec/EmitirRECDialog";
import {
  TIPOS_PRODUCTO_VENTA, TIPOS_PRODUCTO_COMPRA, TIPOS_COMPROBANTE_MANUAL,
  esFiscal, requiereCuit, esFacturaA, UNIDADES_NEGOCIO, EMPTY_FILTERS,
  initForm, TIPOS_COMP_ASOC, estadoBadge, unidadColor,
} from "./facturas/constants";
import DuplicateUploadDialog from "./facturas/DuplicateUploadDialog";
import NCSuccessDialog from "./facturas/NCSuccessDialog";
import RevertBatchDialog from "./facturas/RevertBatchDialog";
import AfipErrorDialog from "./facturas/AfipErrorDialog";
import DeleteFacturaDialog from "./facturas/DeleteFacturaDialog";
import DeleteFacturaCobrosDialog from "./facturas/DeleteFacturaCobrosDialog";
import EmitirNCDialog from "./facturas/EmitirNCDialog";
import FacturaSheetForm from "./facturas/FacturaSheetForm";
import { checkListaIncorrecta } from "@/lib/priceListCheck";
import AlertaListaPrecioModal from "@/components/AlertaListaPrecioModal";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";

export default function FacturasPage({ tipo }) {
  const isVenta = tipo === "venta";
  const { user } = useAuth();
  const defaultUB = user?.unidad_negocio_default || "Mostrador";
  const [facturas, setFacturas] = useState([]);
  // Paginación cliente con persistencia del pageSize.
  const PAGE_SIZE_STORAGE_KEY = `facturas.pageSize.${tipo}`;
  const [pageSize, setPageSize] = useState(() => {
    const stored = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || "50", 10);
    return [20, 50, 100].includes(stored) ? stored : 50;
  });
  const [page, setPage] = useState(1);
  useEffect(() => { try { localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize)); } catch (_) {} }, [pageSize, PAGE_SIZE_STORAGE_KEY]);
  // Ordenamiento cliente con persistencia y multi-columna (Shift+Click).
  // Formato: [{key, dir}] — se aplican en orden (primero el 0, luego el 1, etc).
  const SORT_STORAGE_KEY = `facturas.sort.${tipo}`;
  const [sort, setSort] = useState(() => {
    try {
      const raw = localStorage.getItem(SORT_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length && parsed.every((s) => s && s.key && s.dir)) {
        return parsed;
      }
    } catch (_) { /* ignore */ }
    return [{ key: "fecha", dir: "desc" }];
  });
  useEffect(() => {
    try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort)); } catch (_) { /* ignore */ }
  }, [sort, SORT_STORAGE_KEY]);
  const [contrapartes, setContrapartes] = useState([]);
  const [tiposComprobante, setTiposComprobante] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [productos, setProductos] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initForm({ unidad_negocio: defaultUB }));
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Guarda el motivo de rechazo de AFIP mientras el sheet siga abierto. Se
  // limpia al cambiar a un tipo de comprobante NO fiscal (Remito/Pedido/Ticket).
  const [afipRejection, setAfipRejection] = useState(null);
  const [dupPrompt, setDupPrompt] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [delCobros, setDelCobros] = useState(null); // {factura, cobros[]}
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [batches, setBatches] = useState([]);
  const [tab, setTab] = useState("comprobantes");
  const [delBatch, setDelBatch] = useState(null);
  const [viewBatch, setViewBatch] = useState(null);
  const [afipAuthorizing, setAfipAuthorizing] = useState(null); // id
  const [afipError, setAfipError] = useState(null); // {factura, errores, observaciones}
  const [previewFac, setPreviewFac] = useState(null); // factura a previsualizar (PDF)
  const [ncPrompt, setNcPrompt] = useState(null); // {factura} - factura con CAE que no se puede eliminar
  const [ncMotivo, setNcMotivo] = useState("");
  const [ncEmitting, setNcEmitting] = useState(false);
  const [ncSuccess, setNcSuccess] = useState(null); // {factura_original, nota_credito}
  const [recDialogOpen, setRecDialogOpen] = useState(false);
  const [pendingFacturaPayload, setPendingFacturaPayload] = useState(null);
  // Diálogo "¿Es una venta minorista?" que aparece antes del diálogo REC en Factura A.
  // Si el usuario responde SÍ: se emite Factura A sin REC. Si NO: se abre el REC dialog normal.
  const [minoristaAskOpen, setMinoristaAskOpen] = useState(false);
  const [pendingMinoristaCtx, setPendingMinoristaCtx] = useState(null);
  const [promosAplicables, setPromosAplicables] = useState([]);
  const [promosDescartadas, setPromosDescartadas] = useState(new Set());
  // Cobro pre-existente asociado a la factura que se está editando (para poder
  // borrarlo o reasignarlo si el usuario cambia el medio de pago o el total).
  const [cobroOriginal, setCobroOriginal] = useState(null);
  const fileRef = useRef(null);
  const savingRef = useRef(false);
  // Alerta previa al guardar cuando algún ítem no matchea la lista del cliente.
  // Guardamos el evento sintético para reintentar el flujo tras la confirmación.
  const [listaAlerta, setListaAlerta] = useState(null); // {mismatches, cliente, listaNombre}

  const buildParams = () => {
    const p = { tipo };
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "__all__" && v !== "") p[k] = v;
    });
    return p;
  };

  // Convierte el valor guardado en factura.medio_pago (un caja_id, o "cta_cte", o null)
  // a una etiqueta legible para mostrar en la tabla.
  const labelMedioPago = (mp) => {
    if (!mp || mp === "cta_cte" || mp === "" || mp === null) return "Cta. Corriente";
    // Puede ser un caja_id (nombre físico) o un id de medio de pago virtual (medios_pago con override)
    const caja = cajas.find((c) => c.id === mp);
    if (caja) return caja.nombre || caja.tipo || "Caja";
    const medio = (mediosPago || []).find((m) => m.caja_id === mp || m.id === mp);
    if (medio) return medio.nombre || medio.caja_nombre || "Medio";
    return "—";
  };

  const postCobroAutomatico = async (created, formSnap) => {
    // Las Notas de Crédito no generan movimiento de caja.
    if ((created?.tipo_comprobante || "").toUpperCase().startsWith("NOTA DE CR")) return;
    // Cobro combinado: iteramos las líneas creando N cobros. La porción no cubierta
    // queda como saldo pendiente (cta cte) automáticamente.
    if (Array.isArray(formSnap.pagos_combinados) && formSnap.pagos_combinados.length > 0) {
      let ok = 0;
      let fail = 0;
      for (const p of formSnap.pagos_combinados) {
        if (!p.medio_pago || p.medio_pago === "cta_cte") continue; // sin cobro para cta cte
        const monto = Number(p.monto || 0);
        if (!(monto > 0)) continue;
        const payload = {
          tipo: "cobro",
          fecha: formSnap.fecha,
          contraparte_id: formSnap.contraparte_id,
          caja_id: p.medio_pago,
          monto,
          moneda: formSnap.moneda,
          facturas_aplicadas: [created.id],
          concepto: `Cobro combinado · ${created.tipo_comprobante} ${created.numero}`,
          retenciones: [],
        };
        const cajaSel = cajas.find((c) => c.id === p.medio_pago);
        if (cajaSel?.tipo === "cheques") {
          if (!p.cheque_info) {
            toast.error(`Faltan los datos del cheque para el pago combinado (${cajaSel.nombre})`);
            fail++;
            continue;
          }
          payload.cheque_info = p.cheque_info;
        }
        try {
          await api.post("/cobros-pagos", payload);
          ok++;
        } catch (err) {
          fail++;
          toast.error(err?.response?.data?.detail || `No se pudo registrar el cobro de $${monto}`);
        }
      }
      if (ok > 0) {
        toast.success(`${ok} cobro${ok !== 1 ? "s" : ""} registrado${ok !== 1 ? "s" : ""}${fail > 0 ? ` (${fail} con error)` : ""}`);
      }
      return;
    }

    try {
      const payload = {
        tipo: "cobro",
        fecha: formSnap.fecha,
        contraparte_id: formSnap.contraparte_id,
        caja_id: formSnap.medio_pago,
        monto: created.total,
        moneda: formSnap.moneda,
        facturas_aplicadas: [created.id],
        concepto: `Cobro automático · ${created.tipo_comprobante} ${created.numero}`,
        retenciones: [],
      };
      // Si el medio elegido es una caja tipo "cheques", incluir datos del cheque.
      const cajaSel = cajas.find((c) => c.id === formSnap.medio_pago);
      if (cajaSel?.tipo === "cheques") {
        if (!formSnap.cheque_info) {
          toast.error("Faltan los datos del cheque");
          return;
        }
        payload.cheque_info = formSnap.cheque_info;
      }
      await api.post("/cobros-pagos", payload);
      toast.success(cajaSel?.tipo === "cheques"
        ? "Cheque cargado — quedó registrado en el módulo Cheques Recibidos"
        : "Cobro registrado en la caja");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Factura creada, pero el cobro falló. Registralo manualmente.");
    }
  };

  const registrarConsumoPromos = async (promos) => {
    if (!promos || promos.length === 0) return;
    await Promise.allSettled(
      promos.map((p) =>
        api.post(`/promociones/${p.promocion_id}/consumo`, {
          kilos: p.kilos_a_consumir || 0,
          unidades: p.unidades_a_consumir || 0,
        }),
      ),
    );
  };

  const handleRECAndFacturaAEmit = async (recFormData) => {
    if (!pendingFacturaPayload) return;
    try {
      const { data } = await api.post("/facturas/con-rec", {
        factura: pendingFacturaPayload.payload,
        rec: recFormData,
      });
      const factura = data.factura;
      const rec = data.rec;
      toast.success(
        `Factura A ${factura.numero} · REC ${rec.numero_formateado} autorizados en AFIP`,
        { duration: 8000 },
      );
      // Vincular la factura al pedido de origen (si vino desde Pedidos / Tienda Nube).
      // Idéntica lógica que la del flujo sin REC.
      if (tnVentaIdToLink && factura?.id) {
        try {
          await api.post(`/tiendanube/ventas/${tnVentaIdToLink}/link-factura`, {
            factura_id: factura.id,
            factura_numero: factura.numero || null,
            marcar_confirmado: true,
          });
        } catch (_) {
          toast.warning("Factura creada, pero no se pudo vincular al pedido TN");
        }
        setTnVentaIdToLink(null);
      }
      if (pedidoIdToLink && factura?.id) {
        try {
          await api.post(`/pedidos/${pedidoIdToLink}/link-factura`, {
            factura_id: factura.id,
            factura_numero: factura.numero || null,
            marcar_confirmado: true,
          });
        } catch (_) {
          toast.warning("Factura creada, pero no se pudo vincular al pedido manual");
        }
        setPedidoIdToLink(null);
      }
      // Cobro automático si corresponde (soporta cobro combinado)
      const hasCombinadoRec = Array.isArray(pendingFacturaPayload.pagos_combinados) && pendingFacturaPayload.pagos_combinados.length > 0;
      if (factura?.total > 0 && (hasCombinadoRec || (pendingFacturaPayload.medio_pago && pendingFacturaPayload.medio_pago !== "cta_cte"))) {
        await postCobroAutomatico(factura, pendingFacturaPayload);
      }
      // Registrar consumo de promos (líneas descuento_promo)
      const promosEnPayload = (pendingFacturaPayload.payload?.items || [])
        .filter((it) => it.tipo_producto === "descuento_promo" && it.promocion_id)
        .map((it) => ({ promocion_id: it.promocion_id, kilos_a_consumir: pendingFacturaPayload._kilos_por_promo?.[it.promocion_id] || 0 }));
      if (promosEnPayload.length > 0) {
        await registrarConsumoPromos(promosEnPayload);
      }
      setRecDialogOpen(false);
      setPendingFacturaPayload(null);
      setOpen(false);
      setEditingId(null);
      setForm(initForm({ unidad_negocio: defaultUB }));
      savingRef.current = false;
      load();
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (typeof msg === "object" && msg?.rec) {
        toast.error(
          `Se emitió el REC ${msg.rec.numero_formateado} pero la Factura A fue rechazada por AFIP. Se anuló el REC. Detalle: ${JSON.stringify(msg.detail)}`,
          { duration: 12000 },
        );
      } else {
        toast.error(typeof msg === "string" ? msg : (msg?.message || JSON.stringify(msg) || "Error emitiendo REC+Factura"));
      }
      savingRef.current = false;
    }
  };

  const load = async () => {
    const { data } = await api.get("/facturas", { params: buildParams() });
    setFacturas(data);
    const { data: cps } = await api.get(isVenta ? "/clientes" : "/proveedores");
    setContrapartes(cps);
    const { data: tps } = await api.get("/facturas/tipos-comprobante", { params: { tipo } });
    // Combine known tipos with the ones already present
    setTiposComprobante(Array.from(new Set([...TIPOS_COMPROBANTE_MANUAL, ...tps])).sort());
    const { data: cjs } = await api.get("/cajas");
    setCajas(cjs.filter((c) => c.moneda === "ARS"));
    if (isVenta) {
      // Medios de pago configurados (ámbito ventas o ambos) resueltos por usuario
      try {
        const { data: mp } = await api.get("/medios-pago/mios", { params: { scope: "ventas" } });
        setMediosPago(mp || []);
      } catch { setMediosPago([]); }
    }
    if (isVenta) {
      try {
        const [{ data: ls }, { data: pr }] = await Promise.all([
          api.get("/listas-precios"),
          api.get("/productos"),
        ]);
        setListasPrecios(ls.sort((a, b) => (a.orden || 0) - (b.orden || 0)));
        setProductos(pr);
      } catch (_) {}
    } else {
      // Compras: cargar productos e insumos para el picker dinámico.
      try {
        const [{ data: pr }, { data: ins }] = await Promise.all([
          api.get("/productos"),
          api.get("/insumos"),
        ]);
        setProductos(pr || []);
        setInsumos(ins || []);
      } catch (_) {}
    }
  };

  const loadBatches = async () => {
    const { data } = await api.get("/import-batches");
    // Solo ventas
    setBatches(data.filter((b) => b.type === "ventas"));
  };

  const revertBatch = async () => {
    if (!delBatch) return;
    try {
      const { data } = await api.delete(`/import-batches/${delBatch.id}`);
      toast.success(`Lote revertido: ${data.deleted} facturas eliminadas`);
      setDelBatch(null);
      loadBatches();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al revertir");
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [tipo, filters]);

  useEffect(() => {
    if (isVenta && tab === "historial") loadBatches();
    // eslint-disable-next-line
  }, [tab]);

  // Precargar venta desde un REC emitido (link "Facturar" en RECs → /ventas?rec_id=X)
  const location = useLocation();
  const navigate = useNavigate();
  const [prefilledFromRec, setPrefilledFromRec] = useState(null);
  useEffect(() => {
    if (!isVenta) return;
    const params = new URLSearchParams(location.search);
    const recId = params.get("rec_id");
    if (!recId) return;
    // Evitar re-cargar si ya usamos este rec
    if (prefilledFromRec === recId) return;
    // Esperar a que TODOS los catálogos necesarios estén cargados (productos + contrapartes + listas)
    if (contrapartes.length === 0 || productos.length === 0 || listasPrecios.length === 0) return;

    (async () => {
      try {
        const { data: rec } = await api.get(`/rec/${recId}`);
        // Resolver contraparte: match por CUIT si viene
        let cliente = null;
        const cuitReceptor = rec.receptor?.cuit || rec.cuit_receptor;
        if (cuitReceptor) {
          cliente = contrapartes.find((c) => (c.cuit || "").replace(/-/g, "") === String(cuitReceptor).replace(/-/g, ""));
        }
        // Fallback CF
        if (!cliente) {
          cliente = contrapartes.find((c) => (c.nombre || "").toLowerCase().trim() === "consumidor final");
        }

        // Normalizador de código AFIP: "2.6" == 2.6 == "2.60" == "02.6" → todos deben matchear.
        const normAfip = (v) => {
          if (v === null || v === undefined || v === "") return "";
          const n = parseFloat(v);
          if (isNaN(n)) return String(v).trim();
          // Devolvemos con dos decimales para normalizar "2.6" y "2.60"
          return n.toFixed(2);
        };

        // Normaliza texto para matching por nombre (sin acentos, minúsculas, sólo alfanumérico).
        const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

        const listaId = (cliente?.lista_precios_id) || (listasPrecios[0]?.id) || "";
        let sinMatch = 0;
        const sinPrecioEnLista = [];

        // Mapear items del REC → items de venta usando SIEMPRE la nomenclatura del catálogo local.
        // Si no hay match, la fila queda vacía (sin descripción AFIP) y el usuario debe elegir el producto.
        const items = (rec.mercaderias || []).map((m) => {
          const codAfipRec = normAfip(m.cod_tipo_prod);
          const recDesc = m.descripcion || "";
          // Prioridad 1: match exacto por codigo_afip del catálogo local
          let prod = codAfipRec
            ? productos.find((p) => normAfip(p.codigo_afip) === codAfipRec)
            : null;
          // Prioridad 2: match por nombre (fuzzy inclusion en ambos sentidos)
          if (!prod && recDesc) {
            const nRec = norm(recDesc);
            prod = productos.find((p) => {
              const nProd = norm(p.nombre);
              return nProd && nRec && (nRec.includes(nProd) || nProd.includes(nRec));
            });
          }

          if (!prod) {
            // Sin match: fila vacía, para que el usuario elija el producto del combobox.
            // NO usamos la nomenclatura AFIP como descripción.
            sinMatch += 1;
            return {
              descripcion: "",
              codigo: "",
              tipo_producto: null,
              producto_id: null,
              kilos: parseFloat(m.kilos_neto || m.kilos || 0),
              cantidad: parseFloat(m.cantidad || 1),
              precio_unitario: 0,
            };
          }

          // BUG FIX: precio SIEMPRE resuelto desde la lista del cliente. Nunca cae silenciosamente
          // a otra lista (evita que un producto se traiga con "Minorista" en clientes mayoristas).
          // Fallback: precio_actual sólo si la lista no tiene precio para este producto.
          const precios = prod.precios || {};
          const listPrice = (listaId && precios[listaId]) || 0;
          const precio = listPrice > 0 ? listPrice : (prod.precio_actual || 0);
          if (listaId && !(listPrice > 0)) sinPrecioEnLista.push(prod.nombre || "");

          return {
            descripcion: prod.nombre || "",
            codigo: prod.codigo || "",
            tipo_producto: null,
            producto_id: prod.id,
            kilos: parseFloat(m.kilos_neto || m.kilos || 0),
            cantidad: parseFloat(m.cantidad || 1),
            precio_unitario: precio,
          };
        });

        const base = initForm({ unidad_negocio: defaultUB });
        // Default: Factura A (fiscal) para la venta que factura el REC
        base.tipo_comprobante = "FACTURA A";
        base.contraparte_id = cliente?.id || "";
        base.lista_precios_id = listaId;
        base.items = items.length > 0 ? items : base.items;
        // Fecha de vencimiento: se calcula desde la condicion_pago del cliente
        if (cliente?.condicion_pago) {
          base.fecha_vencimiento = calcVencimiento(base.fecha, cliente.condicion_pago);
        }

        // Normalizar fecha de emisión del REC a "YYYY-MM-DD" (acepta ISO, "YYYYMMDD" y "YYYY-MM-DD")
        const normFechaRec = (v) => {
          if (!v) return "";
          const s = String(v);
          if (s.length >= 10 && s[4] === "-") return s.substring(0, 10);
          if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
          return s.substring(0, 10);
        };
        const recFecha = normFechaRec(rec.fecha_emision || rec.fecha);

        // Asociar el REC a la Factura A como CbtesAsoc (tipo 995 Bovino / 996 Porcino)
        // Este array viaja a AFIP como comprobantes asociados al pedir CAE.
        base.comprobantes_asociados = [
          {
            tipo: parseInt(rec.tipo_cbte, 10) || 995,
            pto_vta: String(rec.punto_emision || ""),
            nro: String(rec.numero || ""),
            fecha: recFecha || base.fecha,
          },
        ];

        // Referencia visible en observaciones
        const recRef = rec.numero_formateado || rec.cod_remito || rec.numero || rec.id;
        base.observaciones = `Facturación del REC ${recRef}`;
        if (recFecha) base.fecha = recFecha;
        setEditingId(null);
        setForm(base);
        setPromosAplicables([]);
        setPromosDescartadas(new Set());
        setOpen(true);
        setPrefilledFromRec(recId);
        toast.success(`Venta pre-cargada como Factura A · REC ${recRef} asociado`);
        if (sinMatch > 0) {
          toast.warning(
            `${sinMatch} ítem${sinMatch > 1 ? "s" : ""} del REC no matcheó con el catálogo. ` +
            `Elegí el producto en el combobox de cada fila vacía.`,
            { duration: 6000 }
          );
        }
        if (sinPrecioEnLista.length > 0) {
          const lista = listasPrecios.find((l) => l.id === listaId);
          toast.warning(
            `${sinPrecioEnLista.length} ítem${sinPrecioEnLista.length > 1 ? "s" : ""} sin precio en "${lista?.nombre || "la lista del cliente"}": ${sinPrecioEnLista.slice(0, 3).join(", ")}${sinPrecioEnLista.length > 3 ? "…" : ""}. Se usó precio_actual. Verificá antes de emitir.`,
            { duration: 8000 }
          );
        }
        // Limpiar el query param para permitir re-navegación futura
        navigate("/ventas", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "No se pudo cargar el REC");
      }
    })();
    // eslint-disable-next-line
  }, [location.search, contrapartes, productos, listasPrecios]);

  // Precargar venta desde un pedido de Tienda Nube (link "Facturar" en TN → /ventas?tn_venta_id=X)
  const [prefilledFromTn, setPrefilledFromTn] = useState(null);
  const [tnVentaIdToLink, setTnVentaIdToLink] = useState(null);
  useEffect(() => {
    if (!isVenta) return;
    const params = new URLSearchParams(location.search);
    const tnId = params.get("tn_venta_id");
    if (!tnId) return;
    if (prefilledFromTn === tnId) return;
    // Esperar catálogos
    if (contrapartes.length === 0 || productos.length === 0 || listasPrecios.length === 0) return;

    (async () => {
      try {
        const { data: v } = await api.get(`/tiendanube/ventas/${tnId}`);

        // Matcher de cliente: primero el `cliente_id` (auto-upsert al sincronizar), luego por CUIT
        let cliente = null;
        let contrapartesActuales = contrapartes;
        if (v.cliente_id) {
          cliente = contrapartesActuales.find((c) => c.id === v.cliente_id) || null;
          // Si el cliente_id apunta a alguien que no está en el listado local (recién creado), re-fetch
          if (!cliente) {
            try {
              const { data: cps } = await api.get("/clientes");
              contrapartesActuales = cps || [];
              setContrapartes(contrapartesActuales);
              cliente = contrapartesActuales.find((c) => c.id === v.cliente_id) || null;
            } catch { /* silencioso */ }
          }
        }
        const idClean = (v.cliente_cuit || "").replace(/\D/g, "");
        if (!cliente && idClean) {
          cliente = contrapartesActuales.find((c) => (c.cuit || "").replace(/\D/g, "") === idClean);
        }
        if (!cliente) {
          cliente = contrapartesActuales.find((c) => (c.nombre || "").toLowerCase().trim() === "consumidor final");
        }

        // Regla: TODO lo que viene de Tienda Nube factura con la lista TN
        // (es_lista_tn), sin importar la lista asignada al cliente.
        const listaTn = listasPrecios.find((l) => l && l.es_lista_tn === true);
        const listaId = (listaTn?.id) || (cliente?.lista_precios_id) || (listasPrecios[0]?.id) || "";

        // Normalizadores para matching de productos por nombre / SKU
        const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

        let sinMatch = 0;
        let excludedSinStock = 0;
        let usedProduccionTn = 0;
        const sinPrecioEnLista = [];
        const items = (v.items || []).filter((it) => {
          if (it.sin_stock) { excludedSinStock += 1; return false; }
          return true;
        }).map((it) => {
          const itemName = it.name || "";
          const itemSku = (it.sku || "").trim();
          // Prioridad 1: match exacto por código del catálogo local (SKU TN == código producto)
          let prod = null;
          if (itemSku) {
            prod = productos.find((p) => (p.codigo || "").trim() === itemSku);
          }
          // Prioridad 2: fuzzy inclusion en el nombre
          if (!prod && itemName) {
            const nIt = norm(itemName);
            prod = productos.find((p) => {
              const nProd = norm(p.nombre);
              return nProd && nIt && (nIt.includes(nProd) || nProd.includes(nIt));
            });
          }

          const qtyOriginal = parseFloat(it.quantity || 1);
          const priceFromOrder = parseFloat(it.price || 0);

          // BUG FIX: precio SIEMPRE resuelto desde la lista del cliente para productos matcheados.
          // Nunca cae silenciosamente a otra lista (evita traer "Minorista" en clientes mayoristas).
          // Si el producto no tiene precio en la lista → fallback: it.price del pedido TN (lo que
          // el cliente ya vio/pagó) y aviso al operador.
          let finalPrice = priceFromOrder;
          if (prod) {
            const listPrice = (listaId && prod.precios) ? prod.precios[listaId] : null;
            if (listPrice && listPrice > 0) {
              finalPrice = listPrice;
            } else if (listaId) {
              sinPrecioEnLista.push(prod.nombre || itemName);
            }
          }

          // Detectar si el producto se vende por kg: por `unidad` del catálogo o por nombre "por kg" en TN
          const nameStr = `${(prod?.nombre) || ""} ${itemName}`.toLowerCase();
          const esPorKg =
            (prod?.unidad || "").toLowerCase().includes("kg") ||
            /\bpor\s*kg\b|\bxkg\b|\b\/kg\b/.test(nameStr);

          // TN: por defecto usa los kilos ESPERADOS. Excepción: si hubo faltante
          // significativo en producción (kg_producidos < 90% del esperado),
          // se facturan los kilos REALMENTE producidos (el resto va a reprogramar).
          const kgProd = (it.kg_producidos !== null && it.kg_producidos !== undefined)
            ? parseFloat(it.kg_producidos) : null;
          let qty = qtyOriginal;
          if (esPorKg && kgProd !== null && qtyOriginal > 0 && kgProd < 0.9 * qtyOriginal) {
            qty = kgProd;
            usedProduccionTn += 1;
          }

          if (!prod) {
            sinMatch += 1;
            return {
              descripcion: itemName,
              codigo: itemSku,
              tipo_producto: null,
              producto_id: null,
              kilos: esPorKg ? qty : 0,
              cantidad: esPorKg ? 1 : qty,
              precio_unitario: finalPrice,
              especificacion: it.especificacion || "",
            };
          }
          return {
            descripcion: prod.nombre || itemName,
            codigo: prod.codigo || itemSku,
            tipo_producto: null,
            producto_id: prod.id,
            kilos: esPorKg ? qty : 0,
            cantidad: esPorKg ? 1 : qty,
            precio_unitario: finalPrice,
            especificacion: it.especificacion || "",
          };
        });

        const base = initForm({ unidad_negocio: defaultUB });
        // Por defecto Factura B (consumidor final). El usuario puede cambiar a A si tiene CUIT.
        base.tipo_comprobante = idClean && idClean.length === 11 ? "FACTURA A" : "FACTURA B";
        base.contraparte_id = cliente?.id || "";
        base.lista_precios_id = listaId;
        // Fecha de vencimiento: se calcula desde la condicion_pago del cliente
        if (cliente?.condicion_pago) {
          base.fecha_vencimiento = calcVencimiento(base.fecha, cliente.condicion_pago);
        }
        // Costo de envío: si el pedido TN tiene un envío cobrado al cliente (envío a
        // domicilio con costo > 0), lo agregamos como línea final para que quede
        // facturado. Se aplica al total y suma IVA como cualquier ítem.
        // Fallback aritmético: TN a veces no manda shipping_cost_customer; deducimos
        // el envío desde total - subtotal + descuento cuando entrega_costo/envio son 0.
        let costoEnvio = parseFloat(v.entrega_costo || v.envio || 0);
        if (!costoEnvio || costoEnvio === 0) {
          const calc = (parseFloat(v.total || 0) - parseFloat(v.subtotal || 0) + parseFloat(v.descuento || 0));
          if (calc > 0.5) costoEnvio = calc;
        }
        const itemsFinales = [...items];
        if (costoEnvio > 0 && v.entrega_tipo === "ship") {
          itemsFinales.push({
            descripcion: `Envío${v.entrega_opcion ? ` · ${v.entrega_opcion}` : ""}`,
            tipo_producto: null,
            codigo: "ENVIO",
            producto_id: null,
            kilos: 0,
            cantidad: 1,
            precio_unitario: costoEnvio,
          });
        }
        base.items = itemsFinales.length > 0
          ? itemsFinales
          : [{ descripcion: "", tipo_producto: "", codigo: "", kilos: 0, cantidad: 1, precio_unitario: 0 }];
        // Fecha: SIEMPRE la de hoy al facturar, sin importar la fecha original del pedido TN.
        // (`initForm({ unidad_negocio: defaultUB })` ya la setea a hoy; no la sobreescribimos con v.fecha.)
        // Medio de pago: si TN dice paid → intentar caja "Efectivo" o "Transferencia" según método
        // sino cta_cte (default). Dejamos cta_cte por seguridad; el usuario lo cambia.
        // Observaciones con la referencia del pedido TN
        const partes = [
          `Pedido Tienda Nube #${v.tn_number || v.tn_order_id}`,
          v.metodo_pago ? `Pago: ${v.metodo_pago}` : null,
          v.entrega_tipo === "pickup" ? "Retiro en local" : (v.entrega_tipo === "ship" ? "Envío a domicilio" : null),
        ].filter(Boolean);
        base.observaciones = partes.join(" · ");
        // Cupón: prioridad al `codigo_promo` cargado manualmente en TN; si no
        // hay, tomamos el primer `cupon_codigos` que trajo el checkout de TN.
        if (v.codigo_promo) {
          base.codigo_promo = String(v.codigo_promo).toUpperCase();
        } else if (v.cupon_codigos && v.cupon_codigos.length > 0) {
          base.codigo_promo = String(v.cupon_codigos[0]).toUpperCase();
        }

        setEditingId(null);
        setForm(base);
        setPromosAplicables([]);
        setPromosDescartadas(new Set());
        setOpen(true);
        setPrefilledFromTn(tnId);
        setTnVentaIdToLink(tnId);
        toast.success(`Venta pre-cargada desde pedido TN #${v.tn_number}`);
        if (usedProduccionTn > 0) {
          toast.info(`${usedProduccionTn} ítem${usedProduccionTn > 1 ? "s" : ""} con faltante >10% — se facturan los kilos producidos y la diferencia queda para reprogramar.`, { duration: 6000 });
        }
        if (excludedSinStock > 0) {
          toast.warning(`${excludedSinStock} ítem${excludedSinStock > 1 ? "s" : ""} marcado${excludedSinStock > 1 ? "s" : ""} SIN STOCK en producción y excluido${excludedSinStock > 1 ? "s" : ""} de la factura.`, { duration: 6000 });
        }
        if (sinMatch > 0) {
          toast.warning(
            `${sinMatch} ítem${sinMatch > 1 ? "s" : ""} del pedido no matchearon con el catálogo. ` +
            `Elegí el producto en el combobox de cada fila.`,
            { duration: 6000 }
          );
        }
        if (sinPrecioEnLista.length > 0) {
          const lista = listasPrecios.find((l) => l.id === listaId);
          toast.warning(
            `${sinPrecioEnLista.length} ítem${sinPrecioEnLista.length > 1 ? "s" : ""} sin precio en "${lista?.nombre || "la lista del cliente"}": ${sinPrecioEnLista.slice(0, 3).join(", ")}${sinPrecioEnLista.length > 3 ? "…" : ""}. Se mantuvo el precio del pedido TN. Verificá antes de emitir.`,
            { duration: 8000 }
          );
        }
        navigate("/ventas", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "No se pudo cargar el pedido de Tienda Nube");
      }
    })();
    // eslint-disable-next-line
  }, [location.search, contrapartes, productos, listasPrecios]);

  // Precargar venta desde un pedido manual (link "Facturar" en /pedidos → /ventas?pedido_id=X)
  const [prefilledFromPedido, setPrefilledFromPedido] = useState(null);
  const [pedidoIdToLink, setPedidoIdToLink] = useState(null);
  useEffect(() => {
    if (!isVenta) return;
    const params = new URLSearchParams(location.search);
    const pedId = params.get("pedido_id");
    if (!pedId) return;
    if (prefilledFromPedido === pedId) return;
    if (contrapartes.length === 0 || productos.length === 0 || listasPrecios.length === 0) return;

    (async () => {
      try {
        const { data: v } = await api.get(`/pedidos/${pedId}`);
        if (v.factura_id && !v.anulada_por_nc_id && v.estado_local !== "refacturacion_por_diferencias") {
          toast.error(`El pedido #${v.numero || ""} ya fue facturado (${v.factura_numero || v.factura_id}). No se puede facturar dos veces.`, { duration: 8000 });
          setPrefilledFromPedido(pedId);
          navigate("/pedidos");
          return;
        }
        let cliente = null;
        let contrapartesActuales = contrapartes;
        if (v.cliente_id) {
          cliente = contrapartesActuales.find((c) => c.id === v.cliente_id) || null;
          if (!cliente) {
            try {
              const { data: cps } = await api.get("/clientes");
              contrapartesActuales = cps || [];
              setContrapartes(contrapartesActuales);
              cliente = contrapartesActuales.find((c) => c.id === v.cliente_id) || null;
            } catch { /* silencioso */ }
          }
        }
        const idClean = (v.cliente_cuit || "").replace(/\D/g, "");
        if (!cliente && idClean) {
          cliente = contrapartesActuales.find((c) => (c.cuit || "").replace(/\D/g, "") === idClean);
        }
        if (!cliente) {
          cliente = contrapartesActuales.find((c) => (c.nombre || "").toLowerCase().trim() === "consumidor final");
        }
        // Regla pedidos manuales/WhatsApp: prioridad a la lista asignada al
        // cliente. Si el cliente NO tiene lista, cae a la lista TN (es_lista_tn).
        // (Los pedidos originados en TN entran por el otro prefill, que fuerza TN.)
        const listaTn = listasPrecios.find((l) => l && l.es_lista_tn === true);
        const listaId = (cliente?.lista_precios_id) || (listaTn?.id) || (listasPrecios[0]?.id) || "";
        const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

        let sinMatch = 0;
        let usedProduccion = 0;
        let excludedSinStock = 0;
        const sinPrecioEnLista = [];
        const items = (v.items || []).filter((it) => {
          if (it.sin_stock) { excludedSinStock += 1; return false; }
          return true;
        }).map((it) => {
          const itemName = it.name || "";
          const itemSku = (it.sku || "").trim();
          let prod = null;
          if (itemSku) prod = productos.find((p) => (p.codigo || "").trim() === itemSku);
          if (!prod && itemName) {
            const nIt = norm(itemName);
            prod = productos.find((p) => {
              const nProd = norm(p.nombre);
              return nProd && nIt && (nIt.includes(nProd) || nProd.includes(nIt));
            });
          }
          const qtyOriginal = parseFloat(it.quantity || 1);
          const priceFromOrder = parseFloat(it.price || 0);

          // BUG FIX: precio SIEMPRE resuelto desde la lista del cliente para productos matcheados.
          // Nunca cae silenciosamente a otra lista (evita traer "Minorista" en clientes mayoristas).
          // Si el producto no tiene precio en la lista → fallback: it.price del pedido + aviso.
          let finalPrice = priceFromOrder;
          if (prod) {
            const listPrice = (listaId && prod.precios) ? prod.precios[listaId] : null;
            if (listPrice && listPrice > 0) {
              finalPrice = listPrice;
            } else if (listaId) {
              sinPrecioEnLista.push(prod.nombre || itemName);
            }
          }

          const nameStr = `${(prod?.nombre) || ""} ${itemName}`.toLowerCase();
          const esPorKg =
            (prod?.unidad || "").toLowerCase().includes("kg") ||
            /\bpor\s*kg\b|\bxkg\b|\b\/kg\b/.test(nameStr);
          // PRIORIDAD (sólo pedidos manuales): peso_real_auditado > kg_producidos > qtyOriginal.
          // Los pedidos TN se prefill siempre con quantity (kg pedidos) — la
          // política del cliente es "TN siempre factura lo pedido".
          const pesoAud = (it.peso_real_auditado !== null && it.peso_real_auditado !== undefined)
            ? parseFloat(it.peso_real_auditado) : null;
          const kgProd = (it.kg_producidos !== null && it.kg_producidos !== undefined)
            ? parseFloat(it.kg_producidos) : null;
          const usarAuditoria = esPorKg && pesoAud !== null && pesoAud >= 0;
          const usarProduccion = !usarAuditoria && esPorKg && kgProd !== null && kgProd > 0;
          if (usarProduccion) usedProduccion += 1;
          const qty = usarAuditoria ? pesoAud : (usarProduccion ? kgProd : qtyOriginal);
          if (!prod) {
            sinMatch += 1;
            return { descripcion: itemName, codigo: itemSku, tipo_producto: null, producto_id: null,
                     kilos: esPorKg ? qty : 0, cantidad: esPorKg ? 1 : qty, precio_unitario: finalPrice };
          }
          return { descripcion: prod.nombre || itemName, codigo: prod.codigo || itemSku, tipo_producto: null, producto_id: prod.id,
                   kilos: esPorKg ? qty : 0, cantidad: esPorKg ? 1 : qty, precio_unitario: finalPrice };
        });

        const base = initForm({ unidad_negocio: defaultUB });
        base.tipo_comprobante = idClean && idClean.length === 11 ? "FACTURA A" : "FACTURA B";
        base.contraparte_id = cliente?.id || "";
        base.lista_precios_id = listaId;
        // Fecha de vencimiento: se calcula a partir de la condicion_pago del cliente
        // (contado / X días / día de la semana). Si no tiene condición, queda = fecha.
        if (cliente?.condicion_pago) {
          base.fecha_vencimiento = calcVencimiento(base.fecha, cliente.condicion_pago);
        }
        // Costo de envío del pedido manual: si tiene un cargo por envío > 0 y es a
        // domicilio, lo sumamos como línea final. Igual comportamiento que TN.
        const costoEnvioPed = parseFloat(v.entrega_costo || v.envio || 0);
        const itemsFinalesPed = [...items];
        if (costoEnvioPed > 0 && v.entrega_tipo === "ship") {
          itemsFinalesPed.push({
            descripcion: `Envío${v.entrega_opcion ? ` · ${v.entrega_opcion}` : ""}`,
            tipo_producto: null,
            codigo: "ENVIO",
            producto_id: null,
            kilos: 0,
            cantidad: 1,
            precio_unitario: costoEnvioPed,
          });
        }
        base.items = itemsFinalesPed.length > 0 ? itemsFinalesPed
          : [{ descripcion: "", tipo_producto: "", codigo: "", kilos: 0, cantidad: 1, precio_unitario: 0 }];
        // Fecha: SIEMPRE hoy al facturar un pedido (no la fecha de creación del pedido).
        // Precargar medio de pago si el pedido lo tiene guardado como caja_id o "cta_cte".
        // Los valores legacy tipo "Efectivo" no matchean ninguna caja y se ignoran (queda cta_cte).
        if (v.metodo_pago) {
          if (v.metodo_pago === "cta_cte") {
            base.medio_pago = "cta_cte";
          } else if (cajas?.some?.((c) => c.id === v.metodo_pago)) {
            base.medio_pago = v.metodo_pago;
          }
        }
        const partes = [
          `Pedido manual #${v.numero}`,
          v.metodo_pago ? `Pago: ${v.metodo_pago}` : null,
          v.entrega_tipo === "pickup" ? "Retiro en local" : (v.entrega_tipo === "ship" ? "Envío a domicilio" : null),
          v.observaciones ? `Obs: ${v.observaciones}` : null,
        ].filter(Boolean);
        base.observaciones = partes.join(" · ");
        // Propagar código de descuento del pedido, si tiene uno cargado.
        if (v.codigo_promo) {
          base.codigo_promo = String(v.codigo_promo).toUpperCase();
        }

        setEditingId(null);
        setForm(base);
        setPromosAplicables([]);
        setPromosDescartadas(new Set());
        setOpen(true);
        setPrefilledFromPedido(pedId);
        setPedidoIdToLink(pedId);
        toast.success(`Venta pre-cargada desde pedido #${v.numero}`);
        if (usedProduccion > 0) {
          toast.info(`${usedProduccion} ítem${usedProduccion > 1 ? "s" : ""} con kilos reales tomados de la Orden de Trabajo.`, { duration: 5000 });
        }
        if (excludedSinStock > 0) {
          toast.warning(`${excludedSinStock} ítem${excludedSinStock > 1 ? "s" : ""} marcado${excludedSinStock > 1 ? "s" : ""} SIN STOCK en producción y excluido${excludedSinStock > 1 ? "s" : ""} de la factura.`, { duration: 6000 });
        }
        if (sinMatch > 0) {
          toast.warning(
            `${sinMatch} ítem${sinMatch > 1 ? "s" : ""} del pedido no matchearon con el catálogo. ` +
            `Elegí el producto en el combobox de cada fila.`,
            { duration: 6000 }
          );
        }
        if (sinPrecioEnLista.length > 0) {
          const lista = listasPrecios.find((l) => l.id === listaId);
          toast.warning(
            `${sinPrecioEnLista.length} ítem${sinPrecioEnLista.length > 1 ? "s" : ""} sin precio en "${lista?.nombre || "la lista del cliente"}": ${sinPrecioEnLista.slice(0, 3).join(", ")}${sinPrecioEnLista.length > 3 ? "…" : ""}. Se mantuvo el precio del pedido. Verificá antes de emitir.`,
            { duration: 8000 }
          );
        }
        {
          const listaUsada = listasPrecios.find((l) => l.id === listaId);
          if (listaUsada) {
            toast.info(`Facturación pre-cargada con la lista "${listaUsada.nombre}".`, { duration: 5000 });
          }
        }
        navigate("/ventas", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "No se pudo cargar el pedido");
      }
    })();
    // eslint-disable-next-line
  }, [location.search, contrapartes, productos, listasPrecios]);

  const setItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm({ ...form, items });
  };

  // Los precios unitarios en items YA INCLUYEN IVA (label "Precio/u c/IVA").
  // Por lo tanto: total c/IVA = sum(qty * precio_unit). El neto se calcula dividiendo.
  const totalConIvaBruto = form.items.reduce((s, it) => s + (parseFloat(it.kilos || it.cantidad || 0) * parseFloat(it.precio_unitario || 0)), 0);
  // Promociones efectivas (aplicables aún no descartadas por el usuario)
  const promosEfectivas = useMemo(
    () => (promosAplicables || []).filter((p) => !promosDescartadas.has(p.promocion_id)),
    [promosAplicables, promosDescartadas],
  );
  const totalDescuentoPromos = promosEfectivas.reduce((s, p) => s + (p.importe_descuento_total || 0), 0);
  const ivaPct = snapIvaAfip(parseFloat(form.iva_pct) || 0);
  const totalConIva = Math.max(0, totalConIvaBruto - totalDescuentoPromos);
  const subtotal = ivaPct > 0 ? totalConIva / (1 + ivaPct / 100) : totalConIva;
  const iva = totalConIva - subtotal;
  const total = totalConIva;

  // Auto-hidratar defaults del form cuando cargan los catálogos DESPUÉS de que
  // el modal se abrió con datos vacíos. Esto evita el race condition donde el
  // usuario hace click en "Nueva" antes de que /clientes o /listas-precios
  // hayan terminado de cargar, dejando el form con contraparte_id/lista vacíos
  // Autopopulación de contraparte por defecto (Consumidor Final) y lista_precios_id.
  // Aplica al abrir el sheet (alta o edición) — sin lista_precios_id el evaluador
  // de promociones no puede matchear promos con lista_precios_ids configuradas.
  useEffect(() => {
    if (!open || !isVenta) return;
    if (form.contraparte_id && form.lista_precios_id) return;
    // Todavía no cargaron los catálogos
    if (contrapartes.length === 0 && listasPrecios.length === 0) return;
    setForm((f) => {
      const patch = {};
      if (!f.contraparte_id && contrapartes.length > 0) {
        const cf = contrapartes.find(
          (c) => (c.nombre || "").toLowerCase().trim() === "consumidor final"
        );
        if (cf) {
          patch.contraparte_id = cf.id;
          if (!f.lista_precios_id) {
            patch.lista_precios_id = getEffectiveListaId(cf, user) || (listasPrecios[0]?.id || "");
          }
        }
      }
      if (!f.lista_precios_id && !patch.lista_precios_id) {
        // Intentamos usar la lista EFECTIVA (override del usuario > lista del cliente).
        const cli = contrapartes.find((c) => c.id === f.contraparte_id);
        patch.lista_precios_id = getEffectiveListaId(cli, user) || listasPrecios[0]?.id || "";
      }
      if (Object.keys(patch).length === 0 || (!patch.contraparte_id && !patch.lista_precios_id)) return f;
      return { ...f, ...patch };
    });
  }, [open, isVenta, contrapartes, listasPrecios, form.contraparte_id, form.lista_precios_id, user]);

  // Evaluar promociones aplicables cuando cambian items / contraparte / medio de pago / fecha / lista.
  // También se evalúan al editar una venta, así los descuentos se recalculan si cambian los ítems.
  useEffect(() => {
    if (!isVenta || !open) {
      if (promosAplicables.length) setPromosAplicables([]);
      return;
    }
    // Construir payload sólo con líneas con producto y cantidad
    const items = form.items
      .filter((it) => (it.descripcion || it.codigo) && (parseFloat(it.kilos) > 0 || parseFloat(it.cantidad) > 0))
      .map((it, idx) => {
        // Fuente de verdad: producto_id guardado en el item (al seleccionar del combobox).
        // Fallback: buscar por codigo (único) en el catálogo.
        let pid = it.producto_id;
        if (!pid && it.codigo) {
          const prod = productos.find((p) => p.codigo === it.codigo);
          pid = prod?.id || null;
        }
        const qty = parseFloat(it.kilos) > 0 ? parseFloat(it.kilos) : parseFloat(it.cantidad || 1);
        return {
          idx,  // preservar índice de línea para poder mostrar qué se descontó
          producto_id: pid,
          descripcion: it.descripcion || it.codigo || "",
          precio_unitario: parseFloat(it.precio_unitario || 0),
          cantidad: qty,
          kilos: parseFloat(it.kilos || 0) || null,
        };
      });
    if (items.length === 0) {
      if (promosAplicables.length) setPromosAplicables([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/promociones/aplicables", {
          cliente_id: form.contraparte_id || null,
          lista_precios_id: form.lista_precios_id || null,
          medio_pago: form.medio_pago || null,
          cobrar_efectivo_en_ruta: !!form.cobrar_efectivo_en_ruta,
          codigo: (form.codigo_promo || "").trim() || null,
          fecha: form.fecha,
          subtotal_factura: subtotal,
          items,
        }, { signal: controller.signal });
        setPromosAplicables(data?.promos || []);
      } catch (err) {
        // silencioso: no interrumpir el flujo si falla
      }
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line
  }, [isVenta, open, editingId, form.items, form.contraparte_id, form.medio_pago, form.cobrar_efectivo_en_ruta, form.codigo_promo, form.fecha, form.lista_precios_id, contrapartes.length, listasPrecios.length]);

  const togglePromo = (id) => {
    const next = new Set(promosDescartadas);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPromosDescartadas(next);
  };

  const openNew = () => {
    setEditingId(null);
    const base = initForm({ unidad_negocio: defaultUB });
    // Predeterminar "Consumidor Final" en ventas si existe en el catálogo
    if (isVenta) {
      const cf = contrapartes.find(
        (c) => (c.nombre || "").toLowerCase().trim() === "consumidor final"
      );
      if (cf) {
        base.contraparte_id = cf.id;
        // También setear la lista de precios: la asignada al cliente, o "Lista Base Minorista" (primera)
        base.lista_precios_id = cf.lista_precios_id || (listasPrecios[0]?.id || "");
      } else if (listasPrecios[0]?.id) {
        // Si no hay CF pero sí lista, arrancar con la primera
        base.lista_precios_id = listasPrecios[0].id;
      }
    }
    setForm(base);
    setPromosAplicables([]);
    setPromosDescartadas(new Set());
    setOpen(true);
  };

  const openEdit = async (f) => {
    setEditingId(f.id);
    // Cargar el cobro asociado (si existe) para saber cómo estaba pagada la factura
    // y poder revertir/reasignar al guardar cambios.
    let cobroAsoc = null;
    if (f.tipo === "venta") {
      try {
        const { data } = await api.get(`/facturas/${f.id}/cobros-asociados`);
        cobroAsoc = (data || []).find((c) => c.tipo === "cobro") || null;
      } catch (_) { /* silencioso */ }
    }
    setCobroOriginal(cobroAsoc);
    // Deducir medio de pago actual: si hay cobro asociado, usar su caja; sino,
    // caer en el campo medio_pago del propio doc; sino cta_cte.
    const medioPagoActual = cobroAsoc?.caja_id || f.medio_pago || "cta_cte";
    // Deducir lista de precios: la del cliente si existe. Necesario para que las
    // promociones con lista_precios_ids se re-evalúen bien en edición.
    const cli = contrapartes.find((c) => c.id === f.contraparte_id);
    const listaId = cli?.lista_precios_id || listasPrecios[0]?.id || "";
    // La UI trabaja siempre con precios c/IVA; en la DB se guarda el precio neto.
    // Al cargar para editar, convertimos neto → c/IVA usando la alícuota efectiva
    // de la factura (deducida de iva/subtotal). Así el usuario ve el mismo precio
    // que puso originalmente y, si guarda sin tocar nada, no se le vuelve a
    // dividir por (1+IVA) al persistir.
    const ivaPctFactura = snapIvaAfip(f.subtotal > 0 ? (f.iva / f.subtotal * 100) : 0);
    const ivaFactorLoad = ivaPctFactura > 0 ? (1 + ivaPctFactura / 100) : 1;
    // Separar líneas reales (productos) de líneas virtuales de descuento por promo.
    // Las de promo no se editan como productos; se recalculan en cada save.
    const itemsReales = (f.items || []).filter((it) => it.tipo_producto !== "descuento_promo");
    setForm({
      numero: f.numero,
      tipo_comprobante: f.tipo_comprobante || "FACTURA B",
      unidad_negocio: f.unidad_negocio || "Mostrador",
      fecha: f.fecha,
      fecha_vencimiento: (f.fecha_vencimiento || f.fecha || "").slice(0, 10),
      contraparte_id: f.contraparte_id,
      moneda: f.moneda || "ARS",
      cotizacion: f.cotizacion || 1,
      iva_pct: ivaPctFactura,
      romaneo: f.romaneo || "",
      observaciones: f.observaciones || "",
      medio_pago: medioPagoActual,
      cheque_info: null,
      lista_precios_id: listaId,
      items: itemsReales.length > 0 ? itemsReales.map((it) => ({
        descripcion: it.descripcion || "",
        tipo_producto: it.tipo_producto || "",
        codigo: it.codigo || "",
        kilos: it.kilos || 0,
        cantidad: it.cantidad || 1,
        precio_unitario: (it.precio_unitario || 0) * ivaFactorLoad,
        producto_id: it.producto_id || null,
      })) : [{ descripcion: "", tipo_producto: "", codigo: "", kilos: 0, cantidad: 1, precio_unitario: 0 }],
      vto_touched: true, // en edición no re-calculamos automáticamente
      comprobantes_asociados: f.comprobantes_asociados || [],
      condicion_iva_receptor_override: f.condicion_iva_receptor_override || "",
      cobrar_efectivo_en_ruta: !!f.cobrar_efectivo_en_ruta,
      codigo_promo: f.codigo_promo || "",
    });
    setOpen(true);
  };

  const save = async (e, bypassListaCheck = false) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    // Guardia contra doble-click / doble-submit (síncrona vía ref + estado UI).
    if (savingRef.current) return;
    savingRef.current = true;
    if (!form.contraparte_id) { savingRef.current = false; return toast.error(isVenta ? "Elegí un cliente" : "Elegí un proveedor"); }
    if (isVenta && !form.unidad_negocio) { savingRef.current = false; return toast.error("Seleccioná la unidad de negocio"); }

    // Alerta: precio no corresponde a la lista asignada al cliente.
    // Sólo para ventas nuevas (no ediciones) y sólo si el cliente tiene lista asignada.
    if (isVenta && !editingId && !bypassListaCheck) {
      const cliObj = contrapartes.find((c) => c.id === form.contraparte_id);
      // Resolvemos la lista EFECTIVA: default del usuario > lista del cliente.
      const listaEfectivaId = getEffectiveListaId(cliObj, user);
      if (listaEfectivaId) {
        // En FacturasPage los precios del form están CON IVA (ver comentario en save).
        const mismatches = checkListaIncorrecta({
          items: form.items,
          cliente: { ...(cliObj || {}), lista_precios_id: listaEfectivaId },
          productos,
          listasPrecios,
          pricesNet: false,
        });
        if (mismatches.length > 0) {
          savingRef.current = false;
          const listaNombre = (listasPrecios.find((l) => l.id === listaEfectivaId) || {}).nombre;
          setListaAlerta({ mismatches, cliente: cliObj, listaNombre });
          return;
        }
      }
    }

    // Validaciones específicas para venta fiscal (se emitirá en AFIP)
    const emitirAFIP = isVenta && !editingId && esFiscal(form.tipo_comprobante);
    const cp = contrapartes.find((c) => c.id === form.contraparte_id);
    if (emitirAFIP && requiereCuit(form.tipo_comprobante)) {
      const cuitDigits = (cp?.cuit || "").replace(/\D/g, "");
      if (cuitDigits.length !== 11) {
        savingRef.current = false;
        return toast.error(
          `${form.tipo_comprobante} requiere CUIT del receptor. Cargalo en el cliente o cambiá a Factura B.`,
        );
      }
    }

    // Nota de Crédito / Débito: AFIP exige CbteAsoc con la factura original.
    // Sin factura asociada válida → error 10063/10064. Bloqueamos antes de enviar.
    const _T = (form.tipo_comprobante || "").toUpperCase().trim();
    const esNC = _T === "NOTA DE CREDITO A" || _T === "NOTA DE CREDITO B" || _T === "NOTA DE CREDITO C";
    const esND = _T === "NOTA DE DEBITO A" || _T === "NOTA DE DEBITO B" || _T === "NOTA DE DEBITO C";
    if (emitirAFIP && (esNC || esND)) {
      const validos = (form.comprobantes_asociados || []).filter(
        (c) => c.tipo && c.pto_vta && c.nro,
      );
      if (validos.length === 0) {
        savingRef.current = false;
        return toast.error(
          `${form.tipo_comprobante} requiere asociar la factura original (tipo, punto de venta y número). AFIP rechaza sin CbteAsoc.`,
          { duration: 8000 },
        );
      }
    }

    // Los precios en UI son c/IVA. Convertimos a NETO para el backend
    // (backend calcula subtotal = sum(items.subtotal) y luego total = subtotal + iva).
    const ivaFactor = ivaPct > 0 ? (1 + ivaPct / 100) : 1;
    const items = form.items.map((it) => {
      const qty = parseFloat(it.kilos) > 0 ? parseFloat(it.kilos) : parseFloat(it.cantidad || 1);
      const puCIva = parseFloat(it.precio_unitario || 0);
      const puNeto = puCIva / ivaFactor;
      const subt = qty * puNeto;
      // Priorizar el iva_pct del producto seleccionado (fuente de verdad para AFIP).
      // Fallback al iva_pct de la factura si el ítem no tiene producto linkeado.
      const prod = it.producto_id ? productos.find((p) => p.id === it.producto_id) : null;
      const ivaPctItem = snapIvaAfip(prod && prod.iva_pct != null ? parseFloat(prod.iva_pct) : ivaPct);
      return {
        descripcion: it.descripcion || "",
        codigo: it.codigo || null,
        tipo_producto: it.tipo_producto || null,
        kilos: parseFloat(it.kilos || 0),
        cantidad: parseFloat(it.cantidad || 1),
        precio_unitario: puNeto,
        subtotal: subt,
        iva_pct: ivaPctItem,
        insumo_id: it.insumo_id || null,
        producto_id: it.producto_id || null,
        categoria_gasto: it.categoria_gasto || null,
      };
    });
    // Inyectar líneas virtuales de descuento por cada promoción efectiva.
    // Aplica tanto al alta como a la edición (así al modificar ítems, el
    // descuento queda alineado con las nuevas condiciones de la venta).
    const promosParaGuardar = isVenta ? promosEfectivas : [];
    for (const promo of promosParaGuardar) {
      // El importe de descuento del promo está en c/IVA, convertimos a neto también
      const impCIva = -Math.abs(promo.importe_descuento_total || 0);
      const impNeto = impCIva / ivaFactor;
      items.push({
        descripcion: `Descuento · ${promo.promocion_nombre}`,
        codigo: null,
        tipo_producto: "descuento_promo",
        kilos: 0,
        cantidad: 1,
        precio_unitario: impNeto,
        subtotal: impNeto,
        promocion_id: promo.promocion_id,
      });
    }
    const payload = {
      tipo,
      // Si es fiscal, el backend sobreescribe con el Nº correlativo de AFIP.
      numero: emitirAFIP ? "AUTO" : form.numero,
      tipo_comprobante: form.tipo_comprobante,
      unidad_negocio: isVenta ? form.unidad_negocio : null,
      fecha: form.fecha,
      fecha_vencimiento: form.fecha_vencimiento || form.fecha,
      contraparte_id: form.contraparte_id, contraparte_nombre: cp?.nombre || "",
      items, iva, iva_pct: ivaPct, moneda: form.moneda, cotizacion: parseFloat(form.cotizacion || 1),
      romaneo: form.romaneo, observaciones: form.observaciones,
      comprobantes_asociados: (form.comprobantes_asociados || []).filter(
        (c) => c.tipo && c.pto_vta && c.nro,
      ),
      actualiza_costo: !isVenta && !!form.actualiza_costo,
      // Si la factura viene de un Pedido/TN venta que ya movió stock en
      // Producción/Mostrador, se lo indicamos al backend para que NO
      // descuente FIFO de nuevo (evita doble descuento).
      origen_pedido_id: isVenta ? (tnVentaIdToLink || pedidoIdToLink || null) : null,
      origen_pedido_tipo: isVenta ? (tnVentaIdToLink ? "tn" : (pedidoIdToLink ? "pedido" : null)) : null,
      // Flag para la Hoja de Reparto: sólo tiene sentido en Ventas cuya factura
      // queda en Cta. Cte. y el chofer va a cobrar el total en efectivo al entregar.
      cobrar_efectivo_en_ruta: isVenta && form.medio_pago === "cta_cte" && !!form.cobrar_efectivo_en_ruta,
      // Override de la condición IVA del receptor sólo para esta factura.
      condicion_iva_receptor_override: form.condicion_iva_receptor_override || null,
      // Código de descuento (cupón) usado al armar la factura. Se guarda como
      // metadata; el descuento ya viene aplicado en los items via `promos_aplicadas`.
      codigo_promo: (form.codigo_promo || "").trim().toUpperCase() || null,
    };
    try {
      setSaving(true);
      if (editingId) {
        const { data: updated } = await api.put(`/facturas/${editingId}`, payload);

        // Si al editar el usuario cambió a un tipo fiscal y la factura NO tiene
        // CAE, disparamos la autorización AFIP automáticamente. Si AFIP rechaza,
        // NO cerramos el sheet y mostramos el banner rojo con el motivo.
        const debeEmitirAfip = isVenta &&
          esFiscal(form.tipo_comprobante) &&
          !(updated?.cae) &&
          !(payload.cae);
        if (debeEmitirAfip) {
          if (requiereCuit(form.tipo_comprobante)) {
            const cuitDigits = (cp?.cuit || "").replace(/\D/g, "");
            if (cuitDigits.length !== 11) {
              const msg = `${form.tipo_comprobante} requiere CUIT del receptor. Cargalo en el cliente o cambiá a Factura B.`;
              setAfipRejection(msg);
              toast.error(msg);
              return; // no cerrar
            }
          }
          try {
            const { data: afipRes } = await api.post(`/afip/facturar/${editingId}`);
            if (afipRes?.ok && afipRes?.cae) {
              toast.success(`Factura autorizada en AFIP · CAE ${afipRes.cae}`, { duration: 6000 });
              avisarFechaAjustada(afipRes.fecha_ajustada);
            } else {
              // AFIP rechazó → banner con motivo, sheet queda abierto
              const errs = [
                ...(afipRes?.errores || []).map((e) => e?.msg || String(e)),
                ...(afipRes?.observaciones || []).map((o) => o?.msg || String(o)),
                afipRes?.excepcion,
              ].filter(Boolean);
              const msg = `AFIP rechazó la factura: ${errs.join(" | ") || "Motivo no informado"}`;
              setAfipRejection(msg);
              toast.error(msg);
              return; // no cerrar
            }
          } catch (afipErr) {
            const detail = afipErr?.response?.data?.detail || "AFIP: error inesperado";
            setAfipRejection(typeof detail === "string" ? detail : "AFIP rechazó la factura");
            toast.error(typeof detail === "string" ? detail : "AFIP rechazó la factura");
            return; // no cerrar
          }
        }

        // Sincronizar el cobro automático con el nuevo medio de pago / total.
        if (isVenta) {
          const nuevoMedio = form.medio_pago || "cta_cte";
          const facturaTotal = updated?.total ?? payload.total ?? 0;
          const cobroPrev = cobroOriginal;

          const facturaCambiaCobro = () => {
            if (!cobroPrev) return nuevoMedio !== "cta_cte" && facturaTotal > 0;
            if (nuevoMedio === "cta_cte") return true; // era cobro y ahora es cta cte
            if (cobroPrev.caja_id !== nuevoMedio) return true; // cambió caja
            if (Math.abs((cobroPrev.monto || 0) - facturaTotal) > 0.01) return true; // cambió monto
            return false;
          };

          if (facturaCambiaCobro()) {
            try {
              // 1. Revertir cobro previo (si había uno)
              if (cobroPrev?.id) {
                await api.delete(`/cobros-pagos/${cobroPrev.id}`);
              }
              // 2. Crear cobro nuevo si el nuevo medio no es cta_cte
              if (nuevoMedio !== "cta_cte" && facturaTotal > 0) {
                await postCobroAutomatico(updated || { ...payload, id: editingId }, form);
              }
            } catch (err) {
              toast.warning(
                err?.response?.data?.detail ||
                "Comprobante actualizado, pero no se pudo sincronizar el cobro asociado. Revisá el módulo Cajas.",
              );
            }
          }
        }
        toast.success("Comprobante actualizado");
      } else {
        // Flujo especial: Factura A con AFIP → primero emitir REC y luego Factura A referenciándolo.
        // ⚠️  Si el usuario ya tiene un REC asociado (venido de "Facturar" desde el módulo RECs),
        //     saltamos el diálogo de emisión y facturamos directo referenciando ese REC.
        const yaTieneREC = (payload.comprobantes_asociados || []).some(
          (c) => c && (parseInt(c.tipo, 10) === 995 || parseInt(c.tipo, 10) === 996)
              && c.pto_vta && c.nro,
        );
        if (emitirAFIP && esFacturaA(form.tipo_comprobante) && !yaTieneREC) {
          const kilosPorPromo = {};
          for (const p of promosParaGuardar) {
            kilosPorPromo[p.promocion_id] = p.kilos_a_consumir || 0;
          }
          // Antes de abrir el diálogo REC, preguntamos si es venta minorista.
          // Si el usuario dice SÍ, saltamos el REC y emitimos Factura A directa.
          setPendingMinoristaCtx({
            payload,
            kilosPorPromo,
            snapshot: {
              medio_pago: form.medio_pago, moneda: form.moneda,
              fecha: form.fecha, contraparte_id: form.contraparte_id,
            },
          });
          setMinoristaAskOpen(true);
          setSaving(false);
          savingRef.current = false;
          return;
        }

        const url = emitirAFIP ? "/facturas?auto_afip=true" : "/facturas";
        const { data: created } = await api.post(url, payload);
        if (created?.cae) {
          toast.success(
            `Factura autorizada en AFIP · Nº ${created.numero} · CAE ${created.cae}`,
            { duration: 6000 },
          );
          avisarFechaAjustada(created?.afip_fecha_ajustada);
        } else {
          toast.success(isVenta ? "Venta registrada" : "Compra registrada");
        }

        // Si vino desde un pedido de Tienda Nube → vincular la factura al pedido
        if (isVenta && tnVentaIdToLink && created?.id) {
          try {
            await api.post(`/tiendanube/ventas/${tnVentaIdToLink}/link-factura`, {
              factura_id: created.id,
              factura_numero: created.numero || null,
              marcar_confirmado: true,
            });
          } catch (e) {
            toast.warning("Factura creada, pero no se pudo vincular al pedido TN");
          }
          setTnVentaIdToLink(null);
        }

        // Si vino desde un pedido manual → vincular la factura al pedido
        if (isVenta && pedidoIdToLink && created?.id) {
          try {
            await api.post(`/pedidos/${pedidoIdToLink}/link-factura`, {
              factura_id: created.id,
              factura_numero: created.numero || null,
              marcar_confirmado: true,
            });
          } catch (e) {
            toast.warning("Factura creada, pero no se pudo vincular al pedido manual");
          }
          setPedidoIdToLink(null);
        }

        // Si eligió una caja como medio de pago (o cobro combinado), generar cobros
        const hasCombinado = Array.isArray(form.pagos_combinados) && form.pagos_combinados.length > 0;
        if (isVenta && created?.total > 0 && (hasCombinado || (form.medio_pago && form.medio_pago !== "cta_cte"))) {
          await postCobroAutomatico(created, form);
        }
        // Registrar consumo de kilos para las promociones aplicadas
        if (isVenta && promosParaGuardar.length > 0) {
          await registrarConsumoPromos(promosParaGuardar);
        }
      }
      setOpen(false);
      setEditingId(null);
      setCobroOriginal(null);
      setForm(initForm({ unidad_negocio: defaultUB }));
      setPromosAplicables([]);
      setPromosDescartadas(new Set());
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error";
      toast.error(detail);
      // Si AFIP rechazó, dejar registrado el motivo para bloquear reintentos
      // hasta que el usuario cambie a un tipo no fiscal (o corrija los datos).
      if (isVenta && esFiscal(form.tipo_comprobante) &&
          typeof detail === "string" && /AFIP\s*(recha|error)/i.test(detail)) {
        setAfipRejection(detail);
      }
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      const { data } = await api.delete(`/facturas/${delTarget.id}`);
      if (data?.anulada_con_nc) {
        const cobrosMsg = data.cantidad_cobros_revertidos > 0
          ? ` · ${data.cantidad_cobros_revertidos} cobro(s) por ${fmtMoney(data.cobros_revertidos || 0)} quedaron a favor del cliente para imputar a otros comprobantes.`
          : "";
        toast.success(
          `${data.tipo_comprobante} ${data.nota_credito_numero} generada.` + cobrosMsg
        );
      } else {
        toast.success("Comprobante eliminado");
      }
      setDelTarget(null);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Factura con CAE: no se puede eliminar. Ofrecer emitir Nota de Crédito.
      if (status === 409 && detail?.requires_nota_credito) {
        setNcPrompt({ factura: detail.factura || delTarget });
        setNcMotivo("");
        setDelTarget(null);
        return;
      }
      if (status === 409 && detail?.cobros) {
        setDelCobros({ factura: delTarget, cobros: detail.cobros });
        setDelTarget(null);
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Error");
    }
  };

  const emitirNotaCredito = async () => {
    if (!ncPrompt?.factura) return;
    setNcEmitting(true);
    try {
      const { data } = await api.post(
        `/facturas/${ncPrompt.factura.id}/nota-credito`,
        { motivo: ncMotivo || null, auto_afip: true }
      );
      setNcSuccess({
        factura_original: ncPrompt.factura,
        nota_credito: data.nota_credito,
      });
      setNcPrompt(null);
      setNcMotivo("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Error emitiendo NC");
    } finally {
      setNcEmitting(false);
    }
  };

  const confirmCascadeDelete = async () => {
    if (!delCobros) return;
    try {
      const { data } = await api.delete(`/facturas/${delCobros.factura.id}?cascade=true`);
      if (data.nota_credito_id) {
        toast.success(`Comprobante eliminado. Se generó una Nota de Crédito automática.`);
      } else {
        toast.success(`Comprobante y ${data.cobros_eliminados} cobro(s) eliminados`);
      }
      setDelCobros(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  // AFIP exige fecha ≥ último comprobante autorizado (correlativo): si la movió, avisamos.
  const avisarFechaAjustada = (aj) => {
    if (!aj) return;
    toast.warning(`Fecha del comprobante ajustada de ${fmtDate(aj.de)} a ${fmtDate(aj.a)}: ${aj.motivo}`, { duration: 10000 });
  };

  const autorizarAFIP = async (f) => {
    setAfipAuthorizing(f.id);
    try {
      const { data } = await api.post(`/afip/facturar/${f.id}`);
      if (data.ok) {
        toast.success(`Factura autorizada. CAE ${data.cae}`);
        avisarFechaAjustada(data.fecha_ajustada);
      } else {
        setAfipError({ factura: f, errores: data.errores || [], observaciones: data.observaciones || [], excepcion: data.excepcion });
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AFIP: error inesperado");
    } finally {
      setAfipAuthorizing(null);
    }
  };

  const descargarPDF = async (f) => {
    try {
      const res = await api.get(`/afip/pdf/${f.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // Liberar la URL después de un rato
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo generar el PDF");
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      // Usar los MISMOS filtros que el listado (buildParams ya filtra "__all__")
      const params = new URLSearchParams(buildParams());
      const res = await api.get(`/facturas/export-excel?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      // extraer filename del header si vino
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `${tipo === "venta" ? "ventas" : "compras"}_export.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success("Excel generado");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo exportar");
    } finally {
      setExporting(false);
    }
  };

  const runImport = async (file, force = false) => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/ventas/import-excel${force ? "?force=true" : ""}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${data.facturas_creadas} ventas · ${data.clientes_creados} clientes · ${data.productos_upsert} productos`);
      setDupPrompt(null);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.batch) {
        setDupPrompt({ file, existing: detail.batch });
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await runImport(file, false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const toggleExpand = (id) => setExpanded({ ...expanded, [id]: !expanded[id] });

  const reset = () => setFilters(EMPTY_FILTERS);
  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const totals = useMemo(() => {
    return facturas.reduce(
      (acc, f) => ({
        subtotal: acc.subtotal + (f.subtotal || 0),
        iva: acc.iva + (f.iva || 0),
        total: acc.total + (f.total || 0),
        kilos: acc.kilos + (f.total_kilos || 0),
      }),
      { subtotal: 0, iva: 0, total: 0, kilos: 0 }
    );
  }, [facturas]);

  const tipos = isVenta ? TIPOS_PRODUCTO_VENTA : TIPOS_PRODUCTO_COMPRA;

  // Ordenamiento cliente sobre las facturas cargadas. Soporta strings, números y fechas.
  const sortValueFor = useCallback((f, key) => {
    switch (key) {
      case "fecha":
      case "fecha_vencimiento":
        return (f[key] || "").slice(0, 10);
      case "tipo_comprobante":
      case "numero":
      case "contraparte_nombre":
      case "unidad_negocio":
      case "estado":
      case "cae":
      case "medio_pago_nombre":
        return String(f[key] || "").toLowerCase();
      case "total_kilos":
      case "subtotal":
      case "iva":
      case "total":
      case "saldo":
        return Number(f[key] || 0);
      default:
        return f[key];
    }
  }, []);

  const facturasOrdenadas = useMemo(() => {
    // 1) Excluir comprobantes de ajuste de la pestaña Ventas (siguen apareciendo en Cta Cte).
    const base = isVenta
      ? facturas.filter((f) => !/^AJUSTE/i.test(f.tipo_comprobante || ""))
      : facturas;
    if (!sort.length) return base;
    return [...base].sort((a, b) => {
      for (const { key, dir } of sort) {
        const va = sortValueFor(a, key);
        const vb = sortValueFor(b, key);
        if (va == null && vb == null) continue;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (va < vb) return dir === "asc" ? -1 : 1;
        if (va > vb) return dir === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [facturas, sort, sortValueFor, isVenta]);

  const totalPaginas = Math.max(1, Math.ceil(facturasOrdenadas.length / pageSize));
  const currentPage = Math.min(page, totalPaginas);
  const facturasPagina = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return facturasOrdenadas.slice(start, start + pageSize);
  }, [facturasOrdenadas, currentPage, pageSize]);

  // Reset de página cuando cambia el total o el sort/pageSize.
  useEffect(() => { setPage(1); }, [facturas.length, pageSize, sort]);

  const toggleSort = (key, e) => {
    const shift = !!(e && (e.shiftKey || e.metaKey));
    setSort((prev) => {
      const idx = prev.findIndex((s) => s.key === key);
      if (shift) {
        // Multi-columna: agrega, alterna dir o remueve si ya está en desc.
        if (idx === -1) return [...prev, { key, dir: "asc" }];
        const cur = prev[idx];
        const next = [...prev];
        if (cur.dir === "asc") next[idx] = { key, dir: "desc" };
        else next.splice(idx, 1); // desc + shift-click → quitar
        return next.length ? next : [{ key: "fecha", dir: "desc" }];
      }
      // Sin shift: reemplaza. Si ya era la única y se clickea de nuevo, alterna dir.
      if (prev.length === 1 && prev[0].key === key) {
        return [{ key, dir: prev[0].dir === "asc" ? "desc" : "asc" }];
      }
      return [{ key, dir: "asc" }];
    });
  };

  const SortIcon = ({ colKey }) => {
    const idx = sort.findIndex((s) => s.key === colKey);
    const active = idx !== -1;
    const dir = active ? sort[idx].dir : null;
    return (
      <span className="inline-flex flex-col ml-1 align-middle relative" style={{ lineHeight: 0.6 }}>
        <span className={`text-[9px] ${active && dir === "asc" ? "text-[#6B1F24]" : "text-neutral-300"}`}>▲</span>
        <span className={`text-[9px] ${active && dir === "desc" ? "text-[#6B1F24]" : "text-neutral-300"}`}>▼</span>
        {/* Índice de prioridad cuando hay multi-columna */}
        {active && sort.length > 1 && (
          <span className="absolute -right-3 top-0 text-[8px] font-mono text-[#6B1F24] font-bold">{idx + 1}</span>
        )}
      </span>
    );
  };

  const SortableTh = ({ colKey, children, className = "", align = "left" }) => (
    <th
      className={`${className} cursor-pointer select-none hover:bg-neutral-100 transition`}
      onClick={(e) => toggleSort(colKey, e)}
      data-testid={`sort-th-${colKey}`}
      title="Click para ordenar · Shift+Click para agregar como criterio secundario"
    >
      <span className={`inline-flex items-center ${align === "right" ? "justify-end w-full" : ""}`}>
        {children}
        <SortIcon colKey={colKey} />
      </span>
    </th>
  );

  return (
    <>
      <Topbar
        title={isVenta ? "Ventas" : "Compras"}
        subtitle={
          isVenta
            ? `${facturas.length} comprobantes · Subtotal ${fmtMoney(totals.subtotal)} · IVA ${fmtMoney(totals.iva)} · Total ${fmtMoney(totals.total)}`
            : "Facturas de proveedores y hacienda."
        }
        right={
          <div className="flex items-center gap-2">
            {isVenta && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="import-ventas-input"
                  onChange={handleImport}
                />
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm h-9"
              onClick={exportExcel}
              disabled={exporting}
              data-testid="export-facturas-btn"
              title="Exportar todos los comprobantes filtrados a Excel"
            >
              <FileXls size={16} className="mr-1 text-emerald-700" />
              {exporting ? "Exportando…" : "Exportar Excel"}
            </Button>
            <Button
              data-testid="new-factura-btn"
              onClick={openNew}
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9"
            >
              <Plus size={16} className="mr-1" /> {isVenta ? "Nueva venta" : "Nueva compra"}
            </Button>
          </div>
        }
      />

      <div className="p-4 space-y-4">
        {isVenta ? (
          <div className="space-y-4">
              {/* Filtros */}
          <div className="border border-[#E5E5E5] bg-white rounded-sm">
            <div className="px-4 py-2 border-b bg-neutral-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FunnelSimple size={14} weight="bold" className="text-[#6B1F24]" />
                <span className="text-[11px] uppercase font-mono tracking-widest text-[#666]">Filtros</span>
              </div>
              {hasFilters && (
                <button
                  data-testid="clear-filters-btn"
                  onClick={reset}
                  className="text-[11px] text-[#666] hover:text-[#6B1F24] flex items-center gap-1 px-2 py-0.5 rounded-sm hover:bg-neutral-100"
                >
                  <X size={12} /> Limpiar
                </button>
              )}
            </div>
            <div className="px-4 py-3 grid grid-cols-12 gap-2 text-[11px]">
              <div className="col-span-2">
                <label className="uppercase font-mono text-[#666] tracking-widest">Tipo comprobante</label>
                <Select value={filters.tipo_comprobante} onValueChange={(v) => setFilters({ ...filters, tipo_comprobante: v })}>
                  <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {tiposComprobante.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="uppercase font-mono text-[#666] tracking-widest">Cliente</label>
                <Select value={filters.contraparte_id} onValueChange={(v) => setFilters({ ...filters, contraparte_id: v })}>
                  <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {contrapartes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="uppercase font-mono text-[#666] tracking-widest">Unidad de negocio</label>
                <Select value={filters.unidad_negocio} onValueChange={(v) => setFilters({ ...filters, unidad_negocio: v })}>
                  <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {UNIDADES_NEGOCIO.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1">
                <label className="uppercase font-mono text-[#666] tracking-widest">Estado</label>
                <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                  <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="parcial">Parcial</SelectItem>
                    <SelectItem value="pagada">Pagada</SelectItem>
                    <SelectItem value="anulada">Anulada</SelectItem>
                    <SelectItem value="aplicada">Aplicada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1">
                <label className="uppercase font-mono text-[#666] tracking-widest">Desde</label>
                <DateInput value={filters.fecha_desde} onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                  className="rounded-sm h-8 mt-1 text-[11px] font-mono" />
              </div>
              <div className="col-span-1">
                <label className="uppercase font-mono text-[#666] tracking-widest">Hasta</label>
                <DateInput value={filters.fecha_hasta} onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                  className="rounded-sm h-8 mt-1 text-[11px] font-mono" />
              </div>
              <div className="col-span-3">
                <label className="uppercase font-mono text-[#666] tracking-widest">Búsqueda libre</label>
                <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  className="rounded-sm h-8 mt-1 text-[12px]" placeholder="Nº, cliente, observaciones…" />
              </div>
            </div>
          </div>

        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] grid-table">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="w-8"></th>
                  <SortableTh colKey="fecha" className="px-3 py-2 text-left w-24">Fecha</SortableTh>
                  <SortableTh colKey="fecha_vencimiento" className="px-3 py-2 text-left w-24">Vto.</SortableTh>
                  <SortableTh colKey="tipo_comprobante" className="px-3 py-2 text-left w-32">Tipo</SortableTh>
                  <SortableTh colKey="numero" className="px-3 py-2 text-left w-36">Nº Comprobante</SortableTh>
                  <SortableTh colKey="contraparte_nombre" className="px-3 py-2 text-left">{isVenta ? "Cliente" : "Proveedor"}</SortableTh>
                  {isVenta && <SortableTh colKey="unidad_negocio" className="px-3 py-2 text-left w-24">U. Negocio</SortableTh>}
                  <SortableTh colKey="total_kilos" className="px-3 py-2 text-right w-20" align="right">Kilos</SortableTh>
                  <SortableTh colKey="subtotal" className="px-3 py-2 text-right w-28" align="right">Sin IVA</SortableTh>
                  <SortableTh colKey="iva" className="px-3 py-2 text-right w-24" align="right">IVA</SortableTh>
                  <SortableTh colKey="total" className="px-3 py-2 text-right w-28" align="right">Total</SortableTh>
                  {!isVenta && <SortableTh colKey="saldo" className="px-3 py-2 text-right w-28" align="right">Saldo</SortableTh>}
                  {isVenta && <SortableTh colKey="medio_pago_nombre" className="px-3 py-2 text-left w-32">Medio de pago</SortableTh>}
                  <SortableTh colKey="estado" className="px-3 py-2 text-left w-20">Estado</SortableTh>
                  {isVenta && <SortableTh colKey="cae" className="px-3 py-2 text-left w-28">AFIP</SortableTh>}
                  <th className="px-3 py-2 text-center w-28">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturasPagina.map((f) => (
                  <Fragment key={f.id}>
                    <tr data-testid={`factura-${f.id}`} className="hover:bg-neutral-50 group">
                      <td className="px-2 text-center">
                        <button
                          data-testid={`expand-${f.id}`}
                          onClick={() => toggleExpand(f.id)}
                          className="p-1 text-[#666] hover:text-[#6B1F24]"
                        >
                          {expanded[f.id] ? <CaretDown size={12} /> : <CaretRight size={12} />}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-mono text-[12px]">{fmtDate(f.fecha)}</td>
                      <td className="px-3 py-2 font-mono text-[12px]">
                        {f.fecha_vencimiento ? (
                          <span className={
                            f.saldo > 0 && f.fecha_vencimiento < todayISO()
                              ? "text-red-600 font-semibold"
                              : ""
                          }>{fmtDate(f.fecha_vencimiento)}</span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200"
                            title="Cliente / proveedor con condición 'sin plazo determinado'"
                          >
                            Sin vencimiento
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[12px] text-[#666]">{f.tipo_comprobante || "-"}</td>
                      <td className="px-3 py-2 font-mono text-[12px]">{f.numero}</td>
                      <td className="px-3 py-2 truncate">{f.contraparte_nombre}</td>
                      {isVenta && (
                        <td className="px-3 py-2">
                          {f.unidad_negocio ? (
                            <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-mono ${unidadColor[f.unidad_negocio] || "bg-neutral-100 text-neutral-700"}`}>
                              {f.unidad_negocio}
                            </span>
                          ) : "-"}
                        </td>
                      )}
                      <td className="px-3 py-2 text-right font-mono">{fmtNum(f.total_kilos, 1)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(f.subtotal, f.moneda)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#666]">{fmtMoney(f.iva, f.moneda)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(f.total, f.moneda)}</td>
                      {!isVenta && <td className="px-3 py-2 text-right font-mono">{fmtMoney(f.saldo, f.moneda)}</td>}
                      {isVenta && (
                        <td className="px-3 py-2 text-[12px]" data-testid={`fac-medio-pago-${f.id}`}>
                          <span className={
                            (!f.medio_pago || f.medio_pago === "cta_cte")
                              ? "inline-block px-1.5 py-0.5 rounded text-[10px] font-mono uppercase bg-amber-50 text-amber-800 border border-amber-200"
                              : "inline-block px-1.5 py-0.5 rounded text-[10px] font-mono uppercase bg-emerald-50 text-emerald-800 border border-emerald-200"
                          }>
                            {labelMedioPago(f.medio_pago)}
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] capitalize font-mono ${estadoBadge[f.estado]}`}>
                          {f.estado}
                        </span>
                      </td>
                      {isVenta && (
                        <td className="px-3 py-2">
                          {f.cae ? (
                            <div className="flex flex-col gap-0.5" data-testid={`fac-cae-${f.id}`}>
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200 w-fit"
                                title={`CAE ${f.cae} · vence ${f.cae_vencimiento || "-"}`}
                              >
                                <ShieldCheck size={10} weight="bold" /> CAE
                              </span>
                              <span className="text-[10px] font-mono text-[#333] tracking-tight">{f.cae}</span>
                              {f.cae_vencimiento && (
                                <span className="text-[9px] font-mono text-[#888] leading-tight">
                                  vto {fmtDate(f.cae_vencimiento)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] font-mono text-[#999]">sin autorizar</span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                          {isVenta && !(f.cae || f.afip_cae || f.afip_numero) && esFiscal(f.tipo_comprobante) && (
                            <button
                              data-testid={`afip-fac-${f.id}`}
                              onClick={() => autorizarAFIP(f)}
                              disabled={afipAuthorizing === f.id}
                              title="Autorizar en AFIP"
                              className="p-1 hover:bg-emerald-50 rounded-sm text-[#666] hover:text-emerald-700 disabled:opacity-40 disabled:cursor-wait"
                            >
                              <Certificate size={13} weight={afipAuthorizing === f.id ? "fill" : "regular"} />
                            </button>
                          )}
                          {isVenta && (
                            <button
                              data-testid={`ver-fac-${f.id}`}
                              onClick={() => setPreviewFac(f)}
                              title="Ver PDF (vista previa)"
                              className="p-1 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-700"
                            >
                              <Eye size={13} weight="regular" />
                            </button>
                          )}
                          {isVenta && (
                            <button
                              data-testid={`pdf-fac-${f.id}`}
                              onClick={() => descargarPDF(f)}
                              title={f.cae ? "Descargar PDF oficial" : "Descargar PDF (no fiscal)"}
                              className="p-1 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-700"
                            >
                              <FilePdf size={13} weight="regular" />
                            </button>
                          )}
                          <button
                            data-testid={`edit-fac-${f.id}`}
                            onClick={() => openEdit(f)}
                            className="p-1 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                          >
                            <PencilSimple size={13} />
                          </button>
                          {(() => {
                            const tc = (f.tipo_comprobante || "").toUpperCase();
                            const esNC = tc.includes("NOTA") && (tc.includes("CREDITO") || tc.includes("CRÉDITO"));
                            const yaAnulada = f.estado === "anulada" || f.anulada_por_nc_id;
                            if (esNC || yaAnulada) {
                              return (
                                <button
                                  disabled
                                  className="p-1 rounded-sm text-neutral-300 cursor-not-allowed"
                                  title={esNC
                                    ? "Las notas de crédito no se pueden eliminar"
                                    : `Ya fue anulada con la NC ${f.anulada_por_nc_numero || ""}`}
                                >
                                  <Trash size={13} />
                                </button>
                              );
                            }
                            return (
                              <button
                                data-testid={`del-fac-${f.id}`}
                                onClick={() => {
                                  if (f.cae) {
                                    setNcPrompt({ factura: f });
                                    setNcMotivo("");
                                  } else {
                                    setDelTarget(f);
                                  }
                                }}
                                className="p-1 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                                title={f.cae ? "Anular con Nota de Crédito" : "Eliminar comprobante"}
                              >
                                <Trash size={13} />
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                    {expanded[f.id] && (
                      <tr>
                        <td colSpan={isVenta ? 16 : 13} className="bg-neutral-50 border-t border-b border-[#E5E5E5]">
                          <div className="p-4 space-y-4">
                            <div>
                              <div className="text-[11px] uppercase font-mono text-[#666] tracking-widest mb-2">
                                Detalle de productos ({f.items?.length || 0})
                              </div>
                              {f.items && f.items.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-[12px]">
                                    <thead className="text-[10px] uppercase font-mono text-[#666]">
                                      <tr>
                                        <th className="px-3 py-1 text-left w-20">Código</th>
                                        <th className="px-3 py-1 text-left">Producto</th>
                                        <th className="px-3 py-1 text-right w-20">Kilos</th>
                                        <th className="px-3 py-1 text-right w-24">P. Unit {esFacturaA(f.tipo_comprobante) ? "s/IVA" : "c/IVA"}</th>
                                        <th className="px-3 py-1 text-right w-16">IVA%</th>
                                        <th className="px-3 py-1 text-right w-28">Sin IVA</th>
                                        <th className="px-3 py-1 text-right w-28">IVA</th>
                                        <th className="px-3 py-1 text-right w-28">Total c/IVA</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {f.items.map((it, i) => {
                                        const rate = it.iva_pct != null ? it.iva_pct : (f.iva_pct || 0);
                                        const neto = it.subtotal || 0;
                                        const ivaLinea = neto * (rate || 0) / 100;
                                        const totalLinea = neto + ivaLinea;
                                        // P. Unit s/IVA para Fac A, c/IVA para el resto
                                        const puDisplay = esFacturaA(f.tipo_comprobante)
                                          ? (it.precio_unitario || 0)
                                          : (it.precio_unitario || 0) * (1 + (rate || 0) / 100);
                                        return (
                                          <tr key={i} className="border-t border-[#E5E5E5]">
                                            <td className="px-3 py-1 font-mono text-[#6B1F24]">{it.codigo || "-"}</td>
                                            <td className="px-3 py-1">{it.descripcion}</td>
                                            <td className="px-3 py-1 text-right font-mono">{fmtNum(it.kilos || it.cantidad, 3)}</td>
                                            <td className="px-3 py-1 text-right font-mono">{fmtMoney(puDisplay, f.moneda)}</td>
                                            <td className="px-3 py-1 text-right font-mono text-[#666]">{fmtNum(rate, 1)}%</td>
                                            <td className="px-3 py-1 text-right font-mono">{fmtMoney(neto, f.moneda)}</td>
                                            <td className="px-3 py-1 text-right font-mono text-[#666]">{fmtMoney(ivaLinea, f.moneda)}</td>
                                            <td className="px-3 py-1 text-right font-mono font-semibold">{fmtMoney(totalLinea, f.moneda)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-[#999] text-[12px]">Sin items detallados.</div>
                              )}
                            </div>

                            {/* Totales generales */}
                            <div className="border border-[#E5E5E5] rounded-sm bg-white p-3 grid grid-cols-2 md:grid-cols-5 gap-4 text-[12px]">
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Kilos totales</div>
                                <div className="font-mono font-semibold mt-1">
                                  {fmtNum(f.items?.reduce((s, it) => s + (parseFloat(it.kilos || it.cantidad) || 0), 0) || 0, 3)} kg
                                </div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Sin IVA</div>
                                <div className="font-mono font-semibold mt-1">{fmtMoney(f.subtotal || 0, f.moneda)}</div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">IVA {fmtNum(f.iva_pct || 0, 1)}%</div>
                                <div className="font-mono font-semibold mt-1">{fmtMoney(f.iva || 0, f.moneda)}</div>
                              </div>
                              {!isVenta && (
                                <div>
                                  <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Percepciones</div>
                                  <div className="font-mono font-semibold mt-1">{fmtMoney(f.percepciones_total || 0, f.moneda)}</div>
                                </div>
                              )}
                              <div className="col-span-1">
                                <div className="text-[9px] uppercase font-mono text-[#6B1F24] tracking-widest">Total</div>
                                <div className="font-mono font-semibold text-[14px] mt-1 text-[#6B1F24]">
                                  {fmtMoney(f.total || 0, f.moneda)}
                                </div>
                              </div>
                            </div>

                            {f.observaciones && (
                              <div className="text-[11px] text-[#666]">
                                <span className="font-mono uppercase text-[10px] tracking-widest">Obs.:</span> {f.observaciones}
                              </div>
                            )}

                            {(f.comprobantes_asociados || []).length > 0 && (
                              <div className="border border-[#6B1F24]/20 rounded-sm bg-[#F5F1E9]/50 p-2">
                                <div className="text-[10px] uppercase font-mono text-[#6B1F24] tracking-widest mb-1.5">
                                  Remitos Cárnicos Asociados
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {(f.comprobantes_asociados || []).map((cba, i) => {
                                    const label = TIPOS_COMP_ASOC.find((t) => t.value === cba.tipo)?.label || `Tipo ${cba.tipo}`;
                                    return (
                                      <div key={i} className="text-[11px] font-mono border border-[#E5E5E5] bg-white px-2 py-1 rounded-sm">
                                        <span className="text-[#6B1F24] font-semibold">{label.split(" ")[0]} {label.match(/\((\d+)\)/)?.[1]}</span>{" "}
                                        <span className="text-[#1A1A1A]">{String(cba.pto_vta).padStart(4, "0")}-{String(cba.nro).padStart(8, "0")}</span>
                                        {cba.fecha && <span className="text-[#666] ml-2">{fmtDate(cba.fecha)}</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {isVenta && f.cae && (
                              <div className="border border-emerald-200 rounded-sm bg-emerald-50/40 p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <ShieldCheck size={14} weight="bold" className="text-emerald-700" />
                                    <span className="font-heading font-semibold text-[13px] text-emerald-800">Autorización AFIP</span>
                                  </div>
                                  <button
                                    data-testid={`pdf-fac-inline-${f.id}`}
                                    onClick={() => descargarPDF(f)}
                                    className="inline-flex items-center gap-1.5 px-3 h-7 border border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white rounded-sm text-[11px] font-mono uppercase tracking-widest transition-colors"
                                  >
                                    <FilePdf size={12} weight="bold" />
                                    Descargar PDF
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                                  <div>
                                    <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">CAE</div>
                                    <div className="font-mono font-semibold">{f.cae}</div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Vto. CAE</div>
                                    <div className="font-mono font-semibold">{fmtDate(f.cae_vencimiento)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Nº Comprobante AFIP</div>
                                    <div className="font-mono font-semibold">
                                      {String(f.afip_punto_venta || "").padStart(4, "0")}-
                                      {String(f.afip_numero || "").padStart(8, "0")}
                                    </div>
                                  </div>
                                  {f.afip_qr_url && (
                                    <div>
                                      <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest mb-1">QR AFIP</div>
                                      <a
                                        href={f.afip_qr_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-block"
                                        title="Abrir QR en AFIP"
                                      >
                                        <img
                                          alt="QR AFIP"
                                          src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(f.afip_qr_url)}`}
                                          className="border border-emerald-200 rounded-sm"
                                        />
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {facturas.length === 0 && (
                  <tr><td colSpan={isVenta ? 15 : 13} className="text-center py-8 text-[#999]">Sin comprobantes.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Paginación */}
          <div className="border-t border-[#E5E5E5] px-4 py-2 flex items-center justify-between text-[12px] flex-wrap gap-2 bg-neutral-50">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono text-[#666] uppercase">Mostrar</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10))}>
                <SelectTrigger className="h-7 w-20 text-[12px] rounded-sm" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[11px] font-mono text-[#666] uppercase">por página</span>
              <span className="text-[11px] text-[#666] ml-3">
                {facturasOrdenadas.length === 0
                  ? "Sin resultados"
                  : `Mostrando ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, facturasOrdenadas.length)} de ${facturasOrdenadas.length}`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(1)}
                disabled={currentPage <= 1}
                className="h-7 px-2 text-[11px]"
                data-testid="page-first"
                title="Primera página"
              >
                «
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="h-7 px-2 text-[11px]"
                data-testid="page-prev"
              >
                ‹ Anterior
              </Button>
              <span className="px-2 font-mono text-[11px] text-[#666]" data-testid="page-info">
                Pág. {currentPage} de {totalPaginas}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                disabled={currentPage >= totalPaginas}
                className="h-7 px-2 text-[11px]"
                data-testid="page-next"
              >
                Siguiente ›
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(totalPaginas)}
                disabled={currentPage >= totalPaginas}
                className="h-7 px-2 text-[11px]"
                data-testid="page-last"
                title="Última página"
              >
                »
              </Button>
            </div>
          </div>
        </div>
            </div>
        ) : (
          <div className="text-center py-8 text-[#999]">Módulo no disponible</div>
        )}
      </div>

      <FacturaSheetForm
        open={open}
        setOpen={setOpen}
        isVenta={isVenta}
        defaultUB={defaultUB}
        editingId={editingId}
        setEditingId={setEditingId}
        saving={saving}
        save={save}
        form={form}
        setForm={setForm}
        setItem={setItem}
        afipRejection={afipRejection}
        setAfipRejection={setAfipRejection}
        contrapartes={contrapartes}
        cajas={cajas}
        mediosPago={mediosPago}
        listasPrecios={listasPrecios}
        productos={productos}
        insumos={insumos}
        promosAplicables={promosAplicables}
        promosDescartadas={promosDescartadas}
        togglePromo={togglePromo}
        promosEfectivas={promosEfectivas}
        totalDescuentoPromos={totalDescuentoPromos}
        subtotal={subtotal}
        iva={iva}
        total={total}
        setCobroOriginal={setCobroOriginal}
      />

      {/* Duplicate upload */}
      <DuplicateUploadDialog
        dupPrompt={dupPrompt}
        onCancel={() => { setDupPrompt(null); if (fileRef.current) fileRef.current.value = ""; }}
        onConfirm={() => runImport(dupPrompt.file, true)}
      />

      {/* Delete / Anular con NC X (ventas sin CAE) */}
      <DeleteFacturaDialog
        delTarget={delTarget}
        isVenta={isVenta}
        onCancel={() => setDelTarget(null)}
        onConfirm={confirmDelete}
      />

      {/* Delete cascade — factura tiene cobros asociados */}
      <DeleteFacturaCobrosDialog
        delCobros={delCobros}
        isVenta={isVenta}
        onCancel={() => setDelCobros(null)}
        onConfirm={confirmCascadeDelete}
      />

      {/* Emitir Nota de Crédito — factura con CAE no se puede eliminar */}
      <EmitirNCDialog
        ncPrompt={ncPrompt}
        ncMotivo={ncMotivo}
        setNcMotivo={setNcMotivo}
        ncEmitting={ncEmitting}
        onCancel={() => { setNcPrompt(null); setNcMotivo(""); }}
        onEmit={emitirNotaCredito}
      />

      {/* NC emitida correctamente */}
      <NCSuccessDialog ncSuccess={ncSuccess} onClose={() => setNcSuccess(null)} />

      {/* Revertir batch de ventas */}
      <RevertBatchDialog
        delBatch={delBatch}
        onCancel={() => setDelBatch(null)}
        onConfirm={revertBatch}
      />

      {viewBatch && (
        <BatchDetailDialog
          batch={viewBatch}
          onClose={() => setViewBatch(null)}
          onChange={() => { load(); loadBatches(); }}
        />
      )}

      {/* AFIP error dialog */}
      <AfipErrorDialog afipError={afipError} onClose={() => setAfipError(null)} />

      {/* Emisión de REC previo a Factura A */}
      <EmitirRECDialog
        open={recDialogOpen}
        onOpenChange={(v) => {
          setRecDialogOpen(v);
          if (!v) setPendingFacturaPayload(null);
        }}
        config={null}
        onSuccess={(recData) => {
          // Este callback normalmente confirma el REC. En este flujo lo interceptamos
          // ANTES de llamar a /rec/emitir: componer los datos y usar /facturas/con-rec.
          // Nota: EmitirRECDialog llama a /rec/emitir internamente. Para el flujo combinado
          // usamos un componente que no dispare la emisión directa — ver useCombinedFlow.
          // (Ver `EmitirRECDialog` para más contexto).
        }}
        onCombinedSubmit={handleRECAndFacturaAEmit}
        factura={pendingFacturaPayload ? {
          id: null,
          contraparte_id: pendingFacturaPayload.contraparte_id,
          fecha: pendingFacturaPayload.fecha,
          items: (pendingFacturaPayload.payload?.items) || pendingFacturaPayload.items || [],
        } : null}
      />

      {/* Pregunta ¿venta minorista? antes de abrir el diálogo REC */}
      <AlertDialog
        open={minoristaAskOpen}
        onOpenChange={(v) => { if (!v) { setMinoristaAskOpen(false); setPendingMinoristaCtx(null); } }}
      >
        <AlertDialogContent data-testid="minorista-ask-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">¿Es una venta minorista?</AlertDialogTitle>
            <AlertDialogDescription>
              Si es una venta al público consumidor final (minorista), se emite Factura A sin
              Remito Electrónico Cárnico asociado. Si es mayorista o traslado con hacienda,
              corresponde emitir el REC previo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-sm"
              onClick={async () => {
                // NO minorista → abre diálogo REC como siempre
                const ctx = pendingMinoristaCtx;
                setMinoristaAskOpen(false);
                setPendingMinoristaCtx(null);
                if (ctx) {
                  setPendingFacturaPayload({
                    payload: ctx.payload,
                    ...ctx.snapshot,
                    _kilos_por_promo: ctx.kilosPorPromo,
                  });
                  setRecDialogOpen(true);
                }
              }}
              data-testid="minorista-ask-no"
            >
              No — emitir REC
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-emerald-700 hover:bg-emerald-800"
              onClick={async () => {
                // SÍ minorista → emitir Factura A directa sin REC
                const ctx = pendingMinoristaCtx;
                setMinoristaAskOpen(false);
                setPendingMinoristaCtx(null);
                if (!ctx) return;
                setSaving(true);
                savingRef.current = true;
                try {
                  const { data: created } = await api.post("/facturas?auto_afip=true", ctx.payload);
                  if (created?.cae) {
                    toast.success(
                      `Factura A minorista autorizada · Nº ${created.numero} · CAE ${created.cae}`,
                      { duration: 6000 },
                    );
                  } else {
                    toast.success(`Factura minorista creada · Nº ${created?.numero || ""}`);
                  }
                  // Vincular al pedido de origen (si vino desde Pedidos / Tienda Nube).
                  if (tnVentaIdToLink && created?.id) {
                    try {
                      await api.post(`/tiendanube/ventas/${tnVentaIdToLink}/link-factura`, {
                        factura_id: created.id, factura_numero: created.numero || null, marcar_confirmado: true,
                      });
                    } catch (_) { /* silencioso */ }
                    setTnVentaIdToLink(null);
                  }
                  if (pedidoIdToLink && created?.id) {
                    try {
                      await api.post(`/pedidos/${pedidoIdToLink}/link-factura`, {
                        factura_id: created.id, factura_numero: created.numero || null, marcar_confirmado: true,
                      });
                    } catch (_) { /* silencioso */ }
                    setPedidoIdToLink(null);
                  }
                  // Cobro automático si corresponde (soporta cobro combinado)
                  const hasCombinadoMin = Array.isArray(ctx.snapshot.pagos_combinados) && ctx.snapshot.pagos_combinados.length > 0;
                  if (hasCombinadoMin || (ctx.snapshot.medio_pago && ctx.snapshot.medio_pago !== "cta_cte")) {
                    try {
                      await postCobroAutomatico(created, {
                        medio_pago: ctx.snapshot.medio_pago,
                        moneda: ctx.snapshot.moneda,
                        fecha: ctx.snapshot.fecha,
                        contraparte_id: ctx.snapshot.contraparte_id,
                        pagos_combinados: ctx.snapshot.pagos_combinados,
                        cheque_info: ctx.snapshot.cheque_info,
                      });
                    } catch (_) { /* silencioso */ }
                  }
                  // Consumir promos (idéntica a la lógica del flujo normal)
                  for (const pid of Object.keys(ctx.kilosPorPromo || {})) {
                    try {
                      await api.post(`/promociones/${pid}/consumo`, {
                        kilos: ctx.kilosPorPromo[pid],
                        factura_id: created?.id,
                      });
                    } catch (_) { /* silencioso */ }
                  }
                  setOpen(false);
                  setEditingId(null);
                  setCobroOriginal(null);
                  setForm(initForm({ unidad_negocio: defaultUB }));
                  setPromosAplicables([]);
                  setPromosDescartadas(new Set());
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Error al emitir la factura minorista");
                } finally {
                  setSaving(false);
                  savingRef.current = false;
                }
              }}
              data-testid="minorista-ask-yes"
            >
              Sí — emitir Factura A directa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Alerta: precio no corresponde a la lista del cliente */}
      <AlertaListaPrecioModal
        open={!!listaAlerta}
        onClose={() => setListaAlerta(null)}
        onConfirm={() => {
          setListaAlerta(null);
          save({ preventDefault: () => {} }, true);
        }}
        cliente={listaAlerta?.cliente}
        listaClienteNombre={listaAlerta?.listaNombre}
        mismatches={listaAlerta?.mismatches || []}
        actionLabel="Facturar igual"
      />
      <PdfPreviewDialog
        open={!!previewFac}
        onOpenChange={(o) => { if (!o) setPreviewFac(null); }}
        url={previewFac ? `/afip/pdf/${previewFac.id}` : null}
        title={previewFac ? `${previewFac.tipo_comprobante || ""} ${previewFac.numero || ""}`.trim() : ""}
        filename={previewFac ? `${previewFac.tipo_comprobante || "comprobante"}_${previewFac.numero || previewFac.id}.pdf`.replace(/[^\w.-]/g, "_") : undefined}
      />
    </>
  );
}
